import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import { Input, RadioButton, TextArea } from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function KYF176() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="container">
                <div className="row border-top text-center">
                  <div className="col-3 border-start border-end border-bottom"></div>
                  <div className="col border-end border-bottom">
                    Totally Dependent
                  </div>
                  <div className="col border-end border-bottom">
                    With Physical Prompts
                  </div>
                  <div className="col border-end border-bottom">
                    With Verbal Prompts
                  </div>
                  <div className="col border-end border-bottom">
                    Independently
                  </div>
                  <div className="col border-end border-bottom">N/A</div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Personal Appearance &amp; Hygiene</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end">Eating</div>
                      <div className="col border-end border-bottom">
                        Uses utensils
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesUtensils", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesUtensils"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesUtensils["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesUtensils", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesUtensils"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesUtensils["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesUtensils", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesUtensils"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesUtensils["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesUtensils", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesUtensils"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesUtensils["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesUtensils", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesUtensils"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesUtensils}
                      value={data.UsesUtensils["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end border-bottom"></div>
                      <div className="col border-end border-bottom">
                        Uses napkin as needed
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesNapkin", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesNapkin"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesNapkin["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesNapkin", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesNapkin"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesNapkin["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesNapkin", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesNapkin"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesNapkin["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesNapkin", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesNapkin"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesNapkin["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesNapkin", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesNapkin"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesNapkin}
                      value={data.UsesNapkin["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end">Toileting</div>
                      <div className="col border-end border-bottom">
                        Uses toilet
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesToilet", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesToilet"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesToilet["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesToilet", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesToilet"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesToilet["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesToilet", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesToilet"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesToilet["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesToilet", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesToilet"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesToilet["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesToilet", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesToilet"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesToilet}
                      value={data.UsesToilet["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end"></div>
                      <div className="col border-end border-bottom">Wipes</div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Wipes", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Wipes"
                      options={[{ _id: "1", name: "" }]}
                      value={data.Wipes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Wipes", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Wipes"
                      options={[{ _id: "2", name: "" }]}
                      value={data.Wipes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Wipes", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Wipes"
                      options={[{ _id: "3", name: "" }]}
                      value={data.Wipes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Wipes", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Wipes"
                      options={[{ _id: "4", name: "" }]}
                      value={data.Wipes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Wipes", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Wipes"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.Wipes}
                      value={data.Wipes["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end border-bottom"></div>
                      <div className="col border-end border-bottom">
                        Washes hands
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesHands", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesHands"
                      options={[{ _id: "1", name: "" }]}
                      value={data.WashesHands["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesHands", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesHands"
                      options={[{ _id: "2", name: "" }]}
                      value={data.WashesHands["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesHands", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesHands"
                      options={[{ _id: "3", name: "" }]}
                      value={data.WashesHands["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesHands", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesHands"
                      options={[{ _id: "4", name: "" }]}
                      value={data.WashesHands["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesHands", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesHands"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.WashesHands}
                      value={data.WashesHands["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end">Bathing</div>
                      <div className="col border-end border-bottom">
                        Water appropriate temperature
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WaterTemp", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WaterTemp"
                      options={[{ _id: "1", name: "" }]}
                      value={data.WaterTemp["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WaterTemp", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WaterTemp"
                      options={[{ _id: "2", name: "" }]}
                      value={data.WaterTemp["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WaterTemp", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WaterTemp"
                      options={[{ _id: "3", name: "" }]}
                      value={data.WaterTemp["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WaterTemp", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WaterTemp"
                      options={[{ _id: "4", name: "" }]}
                      value={data.WaterTemp["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WaterTemp", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WaterTemp"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.WaterTemp}
                      value={data.WaterTemp["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end"></div>
                      <div className="col border-end border-bottom">
                        Cleans self
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansSelf", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansSelf"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CleansSelf["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansSelf", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansSelf"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CleansSelf["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansSelf", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansSelf"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CleansSelf["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansSelf", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansSelf"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CleansSelf["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansSelf", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansSelf"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CleansSelf}
                      value={data.CleansSelf["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end border-bottom"></div>
                      <div className="col border-end border-bottom">
                        Dries self
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DriesSelf", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DriesSelf"
                      options={[{ _id: "1", name: "" }]}
                      value={data.DriesSelf["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DriesSelf", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DriesSelf"
                      options={[{ _id: "2", name: "" }]}
                      value={data.DriesSelf["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DriesSelf", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DriesSelf"
                      options={[{ _id: "3", name: "" }]}
                      value={data.DriesSelf["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DriesSelf", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DriesSelf"
                      options={[{ _id: "4", name: "" }]}
                      value={data.DriesSelf["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DriesSelf", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DriesSelf"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.DriesSelf}
                      value={data.DriesSelf["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end">Clothing</div>
                      <div className="col border-end border-bottom">
                        Puts on correctly
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ClothesCorrect", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ClothesCorrect"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ClothesCorrect["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ClothesCorrect", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ClothesCorrect"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ClothesCorrect["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ClothesCorrect", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ClothesCorrect"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ClothesCorrect["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ClothesCorrect", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ClothesCorrect"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ClothesCorrect["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ClothesCorrect", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ClothesCorrect"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ClothesCorrect}
                      value={data.ClothesCorrect["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end"></div>
                      <div className="col border-end border-bottom">Clean</div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleanClothes", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleanClothes"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CleanClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleanClothes", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleanClothes"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CleanClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleanClothes", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleanClothes"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CleanClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleanClothes", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleanClothes"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CleanClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleanClothes", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleanClothes"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CleanClothes}
                      value={data.CleanClothes["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end"></div>
                      <div className="col border-end border-bottom">
                        Appropriate for weather
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WeatherClothes", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WeatherClothes"
                      options={[{ _id: "1", name: "" }]}
                      value={data.WeatherClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WeatherClothes", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WeatherClothes"
                      options={[{ _id: "2", name: "" }]}
                      value={data.WeatherClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WeatherClothes", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WeatherClothes"
                      options={[{ _id: "3", name: "" }]}
                      value={data.WeatherClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WeatherClothes", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WeatherClothes"
                      options={[{ _id: "4", name: "" }]}
                      value={data.WeatherClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WeatherClothes", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WeatherClothes"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.WeatherClothes}
                      value={data.WeatherClothes["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end"></div>
                      <div className="col border-end border-bottom">
                        Appropriate for occasion
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OccasionClothes", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OccasionClothes"
                      options={[{ _id: "1", name: "" }]}
                      value={data.OccasionClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OccasionClothes", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OccasionClothes"
                      options={[{ _id: "2", name: "" }]}
                      value={data.OccasionClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OccasionClothes", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OccasionClothes"
                      options={[{ _id: "3", name: "" }]}
                      value={data.OccasionClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OccasionClothes", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OccasionClothes"
                      options={[{ _id: "4", name: "" }]}
                      value={data.OccasionClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OccasionClothes", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OccasionClothes"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.OccasionClothes}
                      value={data.OccasionClothes["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end border-bottom"></div>
                      <div className="col border-end border-bottom">
                        Color coordinated
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ColorClothes", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ColorClothes"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ColorClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ColorClothes", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ColorClothes"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ColorClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ColorClothes", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ColorClothes"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ColorClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ColorClothes", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ColorClothes"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ColorClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ColorClothes", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ColorClothes"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ColorClothes}
                      value={data.ColorClothes["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end">Grooming</div>
                      <div className="col border-end border-bottom">
                        Shampoos hair
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ShampoosHair", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ShampoosHair"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ShampoosHair["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ShampoosHair", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ShampoosHair"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ShampoosHair["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ShampoosHair", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ShampoosHair"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ShampoosHair["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ShampoosHair", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ShampoosHair"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ShampoosHair["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ShampoosHair", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ShampoosHair"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ShampoosHair}
                      value={data.ShampoosHair["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end"></div>
                      <div className="col border-end border-bottom">
                        Combs / brushes hair
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CombsHair", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CombsHair"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CombsHair["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CombsHair", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CombsHair"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CombsHair["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CombsHair", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CombsHair"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CombsHair["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CombsHair", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CombsHair"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CombsHair["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CombsHair", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CombsHair"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CombsHair}
                      value={data.CombsHair["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end"></div>
                      <div className="col border-end border-bottom">Shaves</div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Shaves", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Shaves"
                      options={[{ _id: "1", name: "" }]}
                      value={data.Shaves["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Shaves", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Shaves"
                      options={[{ _id: "2", name: "" }]}
                      value={data.Shaves["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Shaves", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Shaves"
                      options={[{ _id: "3", name: "" }]}
                      value={data.Shaves["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Shaves", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Shaves"
                      options={[{ _id: "4", name: "" }]}
                      value={data.Shaves["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Shaves", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Shaves"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.Shaves}
                      value={data.Shaves["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end"></div>
                      <div className="col border-end border-bottom">
                        Nail care
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NailCare", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NailCare"
                      options={[{ _id: "1", name: "" }]}
                      value={data.NailCare["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NailCare", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NailCare"
                      options={[{ _id: "2", name: "" }]}
                      value={data.NailCare["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NailCare", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NailCare"
                      options={[{ _id: "3", name: "" }]}
                      value={data.NailCare["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NailCare", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NailCare"
                      options={[{ _id: "4", name: "" }]}
                      value={data.NailCare["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NailCare", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NailCare"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.NailCare}
                      value={data.NailCare["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end border-bottom"></div>
                      <div className="col border-end border-bottom">
                        Brushes teeth
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BrushesTeeth", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BrushesTeeth"
                      options={[{ _id: "1", name: "" }]}
                      value={data.BrushesTeeth["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BrushesTeeth", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BrushesTeeth"
                      options={[{ _id: "2", name: "" }]}
                      value={data.BrushesTeeth["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BrushesTeeth", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BrushesTeeth"
                      options={[{ _id: "3", name: "" }]}
                      value={data.BrushesTeeth["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BrushesTeeth", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BrushesTeeth"
                      options={[{ _id: "4", name: "" }]}
                      value={data.BrushesTeeth["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BrushesTeeth", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BrushesTeeth"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.BrushesTeeth}
                      value={data.BrushesTeeth["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end">Hygiene</div>
                      <div className="col border-end border-bottom">
                        Uses deodorant
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesDeodorant", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesDeodorant"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesDeodorant["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesDeodorant", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesDeodorant"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesDeodorant["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesDeodorant", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesDeodorant"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesDeodorant["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesDeodorant", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesDeodorant"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesDeodorant["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesDeodorant", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesDeodorant"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesDeodorant}
                      value={data.UsesDeodorant["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end"></div>
                      <div className="col border-end border-bottom">
                        Knows menstrual care
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MenstrualCare", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MenstrualCare"
                      options={[{ _id: "1", name: "" }]}
                      value={data.MenstrualCare["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MenstrualCare", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MenstrualCare"
                      options={[{ _id: "2", name: "" }]}
                      value={data.MenstrualCare["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MenstrualCare", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MenstrualCare"
                      options={[{ _id: "3", name: "" }]}
                      value={data.MenstrualCare["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MenstrualCare", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MenstrualCare"
                      options={[{ _id: "4", name: "" }]}
                      value={data.MenstrualCare["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MenstrualCare", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MenstrualCare"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.MenstrualCare}
                      value={data.MenstrualCare["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-start">
                    <div className="row h-100">
                      <div className="col border-end border-bottom"></div>
                      <div className="col border-end border-bottom">
                        Blows nose as needed
                      </div>
                    </div>
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BlowsNose", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BlowsNose"
                      options={[{ _id: "1", name: "" }]}
                      value={data.BlowsNose["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BlowsNose", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BlowsNose"
                      options={[{ _id: "2", name: "" }]}
                      value={data.BlowsNose["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BlowsNose", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BlowsNose"
                      options={[{ _id: "3", name: "" }]}
                      value={data.BlowsNose["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BlowsNose", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BlowsNose"
                      options={[{ _id: "4", name: "" }]}
                      value={data.BlowsNose["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BlowsNose", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BlowsNose"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.BlowsNose}
                      value={data.BlowsNose["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Health</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Verbalizes symptoms of a cold
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeColdSymptoms",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeColdSymptoms"
                      options={[{ _id: "1", name: "" }]}
                      value={data.VerbalizeColdSymptoms["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeColdSymptoms",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeColdSymptoms"
                      options={[{ _id: "2", name: "" }]}
                      value={data.VerbalizeColdSymptoms["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeColdSymptoms",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeColdSymptoms"
                      options={[{ _id: "3", name: "" }]}
                      value={data.VerbalizeColdSymptoms["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeColdSymptoms",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeColdSymptoms"
                      options={[{ _id: "4", name: "" }]}
                      value={data.VerbalizeColdSymptoms["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeColdSymptoms",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeColdSymptoms"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.VerbalizeColdSymptoms}
                      value={data.VerbalizeColdSymptoms["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Verbalizes treatment of a cold
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeColdTreatment",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeColdTreatment"
                      options={[{ _id: "1", name: "" }]}
                      value={data.VerbalizeColdTreatment["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeColdTreatment",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeColdTreatment"
                      options={[{ _id: "2", name: "" }]}
                      value={data.VerbalizeColdTreatment["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeColdTreatment",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeColdTreatment"
                      options={[{ _id: "3", name: "" }]}
                      value={data.VerbalizeColdTreatment["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeColdTreatment",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeColdTreatment"
                      options={[{ _id: "4", name: "" }]}
                      value={data.VerbalizeColdTreatment["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeColdTreatment",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeColdTreatment"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.VerbalizeColdTreatment}
                      value={data.VerbalizeColdTreatment["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Verbalizes symptoms of the flu
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeFluSymptoms",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeFluSymptoms"
                      options={[{ _id: "1", name: "" }]}
                      value={data.VerbalizeFluSymptoms["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeFluSymptoms",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeFluSymptoms"
                      options={[{ _id: "2", name: "" }]}
                      value={data.VerbalizeFluSymptoms["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeFluSymptoms",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeFluSymptoms"
                      options={[{ _id: "3", name: "" }]}
                      value={data.VerbalizeFluSymptoms["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeFluSymptoms",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeFluSymptoms"
                      options={[{ _id: "4", name: "" }]}
                      value={data.VerbalizeFluSymptoms["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeFluSymptoms",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeFluSymptoms"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.VerbalizeFluSymptoms}
                      value={data.VerbalizeFluSymptoms["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Verbalizes treatment of the flu
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeFluTreatment",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeFluTreatment"
                      options={[{ _id: "1", name: "" }]}
                      value={data.VerbalizeFluTreatment["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeFluTreatment",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeFluTreatment"
                      options={[{ _id: "2", name: "" }]}
                      value={data.VerbalizeFluTreatment["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeFluTreatment",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeFluTreatment"
                      options={[{ _id: "3", name: "" }]}
                      value={data.VerbalizeFluTreatment["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeFluTreatment",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeFluTreatment"
                      options={[{ _id: "4", name: "" }]}
                      value={data.VerbalizeFluTreatment["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeFluTreatment",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeFluTreatment"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.VerbalizeFluTreatment}
                      value={data.VerbalizeFluTreatment["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Verbalizes benefits of rest
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizeRest", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeRest"
                      options={[{ _id: "1", name: "" }]}
                      value={data.VerbalizeRest["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizeRest", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeRest"
                      options={[{ _id: "2", name: "" }]}
                      value={data.VerbalizeRest["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizeRest", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeRest"
                      options={[{ _id: "3", name: "" }]}
                      value={data.VerbalizeRest["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizeRest", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeRest"
                      options={[{ _id: "4", name: "" }]}
                      value={data.VerbalizeRest["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizeRest", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeRest"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.VerbalizeRest}
                      value={data.VerbalizeRest["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Verbalizes benefits of exercise
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeExercise",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeExercise"
                      options={[{ _id: "1", name: "" }]}
                      value={data.VerbalizeExercise["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeExercise",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeExercise"
                      options={[{ _id: "2", name: "" }]}
                      value={data.VerbalizeExercise["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeExercise",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeExercise"
                      options={[{ _id: "3", name: "" }]}
                      value={data.VerbalizeExercise["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeExercise",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeExercise"
                      options={[{ _id: "4", name: "" }]}
                      value={data.VerbalizeExercise["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeExercise",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeExercise"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.VerbalizeExercise}
                      value={data.VerbalizeExercise["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Verbalizes measures to avoid spread of germs
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeAvoidGerms",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeAvoidGerms"
                      options={[{ _id: "1", name: "" }]}
                      value={data.VerbalizeAvoidGerms["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeAvoidGerms",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeAvoidGerms"
                      options={[{ _id: "2", name: "" }]}
                      value={data.VerbalizeAvoidGerms["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeAvoidGerms",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeAvoidGerms"
                      options={[{ _id: "3", name: "" }]}
                      value={data.VerbalizeAvoidGerms["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeAvoidGerms",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeAvoidGerms"
                      options={[{ _id: "4", name: "" }]}
                      value={data.VerbalizeAvoidGerms["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizeAvoidGerms",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizeAvoidGerms"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.VerbalizeAvoidGerms}
                      value={data.VerbalizeAvoidGerms["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Takes own temperature
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OwnTemperature", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OwnTemperature"
                      options={[{ _id: "1", name: "" }]}
                      value={data.OwnTemperature["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OwnTemperature", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OwnTemperature"
                      options={[{ _id: "2", name: "" }]}
                      value={data.OwnTemperature["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OwnTemperature", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OwnTemperature"
                      options={[{ _id: "3", name: "" }]}
                      value={data.OwnTemperature["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OwnTemperature", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OwnTemperature"
                      options={[{ _id: "4", name: "" }]}
                      value={data.OwnTemperature["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OwnTemperature", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OwnTemperature"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.OwnTemperature}
                      value={data.OwnTemperature["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Treats minor cuts and burns
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TreatsCuts", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TreatsCuts"
                      options={[{ _id: "1", name: "" }]}
                      value={data.TreatsCuts["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TreatsCuts", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TreatsCuts"
                      options={[{ _id: "2", name: "" }]}
                      value={data.TreatsCuts["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TreatsCuts", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TreatsCuts"
                      options={[{ _id: "3", name: "" }]}
                      value={data.TreatsCuts["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TreatsCuts", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TreatsCuts"
                      options={[{ _id: "4", name: "" }]}
                      value={data.TreatsCuts["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TreatsCuts", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TreatsCuts"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.TreatsCuts}
                      value={data.TreatsCuts["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Treats twisted ankle
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TreatsAnkle", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TreatsAnkle"
                      options={[{ _id: "1", name: "" }]}
                      value={data.TreatsAnkle["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TreatsAnkle", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TreatsAnkle"
                      options={[{ _id: "2", name: "" }]}
                      value={data.TreatsAnkle["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TreatsAnkle", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TreatsAnkle"
                      options={[{ _id: "3", name: "" }]}
                      value={data.TreatsAnkle["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TreatsAnkle", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TreatsAnkle"
                      options={[{ _id: "4", name: "" }]}
                      value={data.TreatsAnkle["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TreatsAnkle", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TreatsAnkle"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.TreatsAnkle}
                      value={data.TreatsAnkle["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Cares for person with seizure
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SeizureCare", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SeizureCare"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SeizureCare["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SeizureCare", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SeizureCare"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SeizureCare["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SeizureCare", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SeizureCare"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SeizureCare["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SeizureCare", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SeizureCare"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SeizureCare["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SeizureCare", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SeizureCare"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SeizureCare}
                      value={data.SeizureCare["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Knows when to call doctor
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CallDoctor", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CallDoctor"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CallDoctor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CallDoctor", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CallDoctor"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CallDoctor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CallDoctor", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CallDoctor"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CallDoctor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CallDoctor", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CallDoctor"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CallDoctor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CallDoctor", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CallDoctor"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CallDoctor}
                      value={data.CallDoctor["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Goes to doctor appointments
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AttendAppt", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AttendAppt"
                      options={[{ _id: "1", name: "" }]}
                      value={data.AttendAppt["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AttendAppt", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AttendAppt"
                      options={[{ _id: "2", name: "" }]}
                      value={data.AttendAppt["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AttendAppt", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AttendAppt"
                      options={[{ _id: "3", name: "" }]}
                      value={data.AttendAppt["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AttendAppt", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AttendAppt"
                      options={[{ _id: "4", name: "" }]}
                      value={data.AttendAppt["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AttendAppt", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AttendAppt"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.AttendAppt}
                      value={data.AttendAppt["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Picks up medication
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "GetMedication", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="GetMedication"
                      options={[{ _id: "1", name: "" }]}
                      value={data.GetMedication["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "GetMedication", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="GetMedication"
                      options={[{ _id: "2", name: "" }]}
                      value={data.GetMedication["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "GetMedication", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="GetMedication"
                      options={[{ _id: "3", name: "" }]}
                      value={data.GetMedication["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "GetMedication", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="GetMedication"
                      options={[{ _id: "4", name: "" }]}
                      value={data.GetMedication["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "GetMedication", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="GetMedication"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.GetMedication}
                      value={data.GetMedication["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Sets up medication
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetMedication", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetMedication"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SetMedication["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetMedication", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetMedication"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SetMedication["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetMedication", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetMedication"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SetMedication["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetMedication", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetMedication"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SetMedication["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetMedication", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetMedication"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SetMedication}
                      value={data.SetMedication["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses medication safety
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseMedication", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseMedication"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UseMedication["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseMedication", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseMedication"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UseMedication["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseMedication", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseMedication"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UseMedication["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseMedication", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseMedication"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UseMedication["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseMedication", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseMedication"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UseMedication}
                      value={data.UseMedication["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Follows directions of medication
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "FollowMedicationDirections",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowMedicationDirections"
                      options={[{ _id: "1", name: "" }]}
                      value={data.FollowMedicationDirections["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "FollowMedicationDirections",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowMedicationDirections"
                      options={[{ _id: "2", name: "" }]}
                      value={data.FollowMedicationDirections["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "FollowMedicationDirections",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowMedicationDirections"
                      options={[{ _id: "3", name: "" }]}
                      value={data.FollowMedicationDirections["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "FollowMedicationDirections",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowMedicationDirections"
                      options={[{ _id: "4", name: "" }]}
                      value={data.FollowMedicationDirections["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "FollowMedicationDirections",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowMedicationDirections"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.FollowMedicationDirections}
                      value={data.FollowMedicationDirections["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Housekeeping Skills</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Initiates own laundry
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StartsLaundry", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StartsLaundry"
                      options={[{ _id: "1", name: "" }]}
                      value={data.StartsLaundry["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StartsLaundry", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StartsLaundry"
                      options={[{ _id: "2", name: "" }]}
                      value={data.StartsLaundry["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StartsLaundry", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StartsLaundry"
                      options={[{ _id: "3", name: "" }]}
                      value={data.StartsLaundry["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StartsLaundry", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StartsLaundry"
                      options={[{ _id: "4", name: "" }]}
                      value={data.StartsLaundry["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StartsLaundry", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StartsLaundry"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.StartsLaundry}
                      value={data.StartsLaundry["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Sorts colors, fabrics, etc.
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SortsLaundry", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SortsLaundry"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SortsLaundry["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SortsLaundry", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SortsLaundry"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SortsLaundry["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SortsLaundry", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SortsLaundry"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SortsLaundry["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SortsLaundry", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SortsLaundry"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SortsLaundry["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SortsLaundry", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SortsLaundry"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SortsLaundry}
                      value={data.SortsLaundry["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Sets washer correctly
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetWasher", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetWasher"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SetWasher["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetWasher", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetWasher"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SetWasher["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetWasher", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetWasher"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SetWasher["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetWasher", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetWasher"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SetWasher["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetWasher", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetWasher"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SetWasher}
                      value={data.SetWasher["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Measures detergent
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MeasureDetergent", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MeasureDetergent"
                      options={[{ _id: "1", name: "" }]}
                      value={data.MeasureDetergent["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MeasureDetergent", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MeasureDetergent"
                      options={[{ _id: "2", name: "" }]}
                      value={data.MeasureDetergent["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MeasureDetergent", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MeasureDetergent"
                      options={[{ _id: "3", name: "" }]}
                      value={data.MeasureDetergent["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MeasureDetergent", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MeasureDetergent"
                      options={[{ _id: "4", name: "" }]}
                      value={data.MeasureDetergent["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MeasureDetergent", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MeasureDetergent"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.MeasureDetergent}
                      value={data.MeasureDetergent["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses bleach appropriately
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseBleach", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseBleach"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UseBleach["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseBleach", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseBleach"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UseBleach["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseBleach", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseBleach"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UseBleach["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseBleach", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseBleach"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UseBleach["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseBleach", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseBleach"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UseBleach}
                      value={data.UseBleach["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Sets dryer correctly
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetDryer", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetDryer"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SetDryer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetDryer", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetDryer"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SetDryer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetDryer", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetDryer"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SetDryer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetDryer", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetDryer"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SetDryer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetDryer", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetDryer"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SetDryer}
                      value={data.SetDryer["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Cleans lint trap
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansLint", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansLint"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CleansLint["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansLint", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansLint"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CleansLint["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansLint", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansLint"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CleansLint["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansLint", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansLint"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CleansLint["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansLint", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansLint"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CleansLint}
                      value={data.CleansLint["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Removes items when dry
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RemovesDry", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RemovesDry"
                      options={[{ _id: "1", name: "" }]}
                      value={data.RemovesDry["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RemovesDry", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RemovesDry"
                      options={[{ _id: "2", name: "" }]}
                      value={data.RemovesDry["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RemovesDry", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RemovesDry"
                      options={[{ _id: "3", name: "" }]}
                      value={data.RemovesDry["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RemovesDry", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RemovesDry"
                      options={[{ _id: "4", name: "" }]}
                      value={data.RemovesDry["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RemovesDry", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RemovesDry"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.RemovesDry}
                      value={data.RemovesDry["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Hangs clothes as needed
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "HangsClothes", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="HangsClothes"
                      options={[{ _id: "1", name: "" }]}
                      value={data.HangsClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "HangsClothes", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="HangsClothes"
                      options={[{ _id: "2", name: "" }]}
                      value={data.HangsClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "HangsClothes", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="HangsClothes"
                      options={[{ _id: "3", name: "" }]}
                      value={data.HangsClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "HangsClothes", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="HangsClothes"
                      options={[{ _id: "4", name: "" }]}
                      value={data.HangsClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "HangsClothes", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="HangsClothes"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.HangsClothes}
                      value={data.HangsClothes["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Folds clothes
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FoldsClothes", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FoldsClothes"
                      options={[{ _id: "1", name: "" }]}
                      value={data.FoldsClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FoldsClothes", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FoldsClothes"
                      options={[{ _id: "2", name: "" }]}
                      value={data.FoldsClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FoldsClothes", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FoldsClothes"
                      options={[{ _id: "3", name: "" }]}
                      value={data.FoldsClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FoldsClothes", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FoldsClothes"
                      options={[{ _id: "4", name: "" }]}
                      value={data.FoldsClothes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FoldsClothes", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FoldsClothes"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.FoldsClothes}
                      value={data.FoldsClothes["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Makes bed
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesBed", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesBed"
                      options={[{ _id: "1", name: "" }]}
                      value={data.MakesBed["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesBed", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesBed"
                      options={[{ _id: "2", name: "" }]}
                      value={data.MakesBed["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesBed", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesBed"
                      options={[{ _id: "3", name: "" }]}
                      value={data.MakesBed["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesBed", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesBed"
                      options={[{ _id: "4", name: "" }]}
                      value={data.MakesBed["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesBed", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesBed"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.MakesBed}
                      value={data.MakesBed["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Dusts
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Dusts", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Dusts"
                      options={[{ _id: "1", name: "" }]}
                      value={data.Dusts["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Dusts", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Dusts"
                      options={[{ _id: "2", name: "" }]}
                      value={data.Dusts["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Dusts", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Dusts"
                      options={[{ _id: "3", name: "" }]}
                      value={data.Dusts["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Dusts", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Dusts"
                      options={[{ _id: "4", name: "" }]}
                      value={data.Dusts["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Dusts", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Dusts"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.Dusts}
                      value={data.Dusts["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Empties wastebaskets
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "EmptiesWastebasket",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="EmptiesWastebasket"
                      options={[{ _id: "1", name: "" }]}
                      value={data.EmptiesWastebasket["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "EmptiesWastebasket",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="EmptiesWastebasket"
                      options={[{ _id: "2", name: "" }]}
                      value={data.EmptiesWastebasket["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "EmptiesWastebasket",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="EmptiesWastebasket"
                      options={[{ _id: "3", name: "" }]}
                      value={data.EmptiesWastebasket["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "EmptiesWastebasket",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="EmptiesWastebasket"
                      options={[{ _id: "4", name: "" }]}
                      value={data.EmptiesWastebasket["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "EmptiesWastebasket",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="EmptiesWastebasket"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.EmptiesWastebasket}
                      value={data.EmptiesWastebasket["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Vacuums
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Vacuums", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Vacuums"
                      options={[{ _id: "1", name: "" }]}
                      value={data.Vacuums["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Vacuums", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Vacuums"
                      options={[{ _id: "2", name: "" }]}
                      value={data.Vacuums["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Vacuums", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Vacuums"
                      options={[{ _id: "3", name: "" }]}
                      value={data.Vacuums["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Vacuums", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Vacuums"
                      options={[{ _id: "4", name: "" }]}
                      value={data.Vacuums["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Vacuums", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Vacuums"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.Vacuums}
                      value={data.Vacuums["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Cleans mirrors
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansMirrors", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansMirrors"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CleansMirrors["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansMirrors", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansMirrors"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CleansMirrors["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansMirrors", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansMirrors"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CleansMirrors["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansMirrors", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansMirrors"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CleansMirrors["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansMirrors", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansMirrors"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CleansMirrors}
                      value={data.CleansMirrors["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Chooses appropriate decorations
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateDecor", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateDecor"
                      options={[{ _id: "1", name: "" }]}
                      value={data.AppropriateDecor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateDecor", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateDecor"
                      options={[{ _id: "2", name: "" }]}
                      value={data.AppropriateDecor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateDecor", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateDecor"
                      options={[{ _id: "3", name: "" }]}
                      value={data.AppropriateDecor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateDecor", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateDecor"
                      options={[{ _id: "4", name: "" }]}
                      value={data.AppropriateDecor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateDecor", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateDecor"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.AppropriateDecor}
                      value={data.AppropriateDecor["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Cleans toilet
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansToilet", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansToilet"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CleansToilet["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansToilet", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansToilet"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CleansToilet["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansToilet", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansToilet"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CleansToilet["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansToilet", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansToilet"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CleansToilet["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansToilet", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansToilet"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CleansToilet}
                      value={data.CleansToilet["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Cleans bathtub
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansBathtub", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansBathtub"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CleansBathtub["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansBathtub", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansBathtub"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CleansBathtub["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansBathtub", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansBathtub"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CleansBathtub["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansBathtub", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansBathtub"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CleansBathtub["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansBathtub", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansBathtub"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CleansBathtub}
                      value={data.CleansBathtub["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Cleans sink
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansSink", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansSink"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CleansSink["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansSink", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansSink"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CleansSink["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansSink", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansSink"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CleansSink["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansSink", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansSink"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CleansSink["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansSink", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansSink"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CleansSink}
                      value={data.CleansSink["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Washes towels
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesTowels", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesTowels"
                      options={[{ _id: "1", name: "" }]}
                      value={data.WashesTowels["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesTowels", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesTowels"
                      options={[{ _id: "2", name: "" }]}
                      value={data.WashesTowels["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesTowels", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesTowels"
                      options={[{ _id: "3", name: "" }]}
                      value={data.WashesTowels["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesTowels", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesTowels"
                      options={[{ _id: "4", name: "" }]}
                      value={data.WashesTowels["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesTowels", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesTowels"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.WashesTowels}
                      value={data.WashesTowels["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Disposes of hygiene items properly
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "DisposeHygieneItems",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="DisposeHygieneItems"
                      options={[{ _id: "1", name: "" }]}
                      value={data.DisposeHygieneItems["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "DisposeHygieneItems",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="DisposeHygieneItems"
                      options={[{ _id: "2", name: "" }]}
                      value={data.DisposeHygieneItems["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "DisposeHygieneItems",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="DisposeHygieneItems"
                      options={[{ _id: "3", name: "" }]}
                      value={data.DisposeHygieneItems["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "DisposeHygieneItems",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="DisposeHygieneItems"
                      options={[{ _id: "4", name: "" }]}
                      value={data.DisposeHygieneItems["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "DisposeHygieneItems",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="DisposeHygieneItems"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.DisposeHygieneItems}
                      value={data.DisposeHygieneItems["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Empties garbage
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "EmptiesGarbage", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="EmptiesGarbage"
                      options={[{ _id: "1", name: "" }]}
                      value={data.EmptiesGarbage["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "EmptiesGarbage", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="EmptiesGarbage"
                      options={[{ _id: "2", name: "" }]}
                      value={data.EmptiesGarbage["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "EmptiesGarbage", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="EmptiesGarbage"
                      options={[{ _id: "3", name: "" }]}
                      value={data.EmptiesGarbage["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "EmptiesGarbage", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="EmptiesGarbage"
                      options={[{ _id: "4", name: "" }]}
                      value={data.EmptiesGarbage["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "EmptiesGarbage", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="EmptiesGarbage"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.EmptiesGarbage}
                      value={data.EmptiesGarbage["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Sweeps floor
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SweepsFloor", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SweepsFloor"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SweepsFloor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SweepsFloor", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SweepsFloor"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SweepsFloor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SweepsFloor", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SweepsFloor"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SweepsFloor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SweepsFloor", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SweepsFloor"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SweepsFloor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SweepsFloor", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SweepsFloor"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SweepsFloor}
                      value={data.SweepsFloor["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Mops floor
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MopsFloor", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MopsFloor"
                      options={[{ _id: "1", name: "" }]}
                      value={data.MopsFloor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MopsFloor", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MopsFloor"
                      options={[{ _id: "2", name: "" }]}
                      value={data.MopsFloor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MopsFloor", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MopsFloor"
                      options={[{ _id: "3", name: "" }]}
                      value={data.MopsFloor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MopsFloor", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MopsFloor"
                      options={[{ _id: "4", name: "" }]}
                      value={data.MopsFloor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MopsFloor", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MopsFloor"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.MopsFloor}
                      value={data.MopsFloor["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Washes dishes
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesDishes", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesDishes"
                      options={[{ _id: "1", name: "" }]}
                      value={data.WashesDishes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesDishes", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesDishes"
                      options={[{ _id: "2", name: "" }]}
                      value={data.WashesDishes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesDishes", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesDishes"
                      options={[{ _id: "3", name: "" }]}
                      value={data.WashesDishes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesDishes", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesDishes"
                      options={[{ _id: "4", name: "" }]}
                      value={data.WashesDishes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WashesDishes", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WashesDishes"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.WashesDishes}
                      value={data.WashesDishes["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Operates dishwasher
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "OperatesDishwasher",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="OperatesDishwasher"
                      options={[{ _id: "1", name: "" }]}
                      value={data.OperatesDishwasher["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "OperatesDishwasher",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="OperatesDishwasher"
                      options={[{ _id: "2", name: "" }]}
                      value={data.OperatesDishwasher["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "OperatesDishwasher",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="OperatesDishwasher"
                      options={[{ _id: "3", name: "" }]}
                      value={data.OperatesDishwasher["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "OperatesDishwasher",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="OperatesDishwasher"
                      options={[{ _id: "4", name: "" }]}
                      value={data.OperatesDishwasher["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "OperatesDishwasher",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="OperatesDishwasher"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.OperatesDishwasher}
                      value={data.OperatesDishwasher["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Puts dishes away
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PutAwayDishes", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PutAwayDishes"
                      options={[{ _id: "1", name: "" }]}
                      value={data.PutAwayDishes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PutAwayDishes", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PutAwayDishes"
                      options={[{ _id: "2", name: "" }]}
                      value={data.PutAwayDishes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PutAwayDishes", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PutAwayDishes"
                      options={[{ _id: "3", name: "" }]}
                      value={data.PutAwayDishes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PutAwayDishes", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PutAwayDishes"
                      options={[{ _id: "4", name: "" }]}
                      value={data.PutAwayDishes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PutAwayDishes", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PutAwayDishes"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.PutAwayDishes}
                      value={data.PutAwayDishes["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Cleans table
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansTable", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansTable"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CleansTable["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansTable", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansTable"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CleansTable["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansTable", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansTable"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CleansTable["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansTable", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansTable"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CleansTable["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansTable", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansTable"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CleansTable}
                      value={data.CleansTable["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Cleans counter
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansCounter", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansCounter"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CleansCounter["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansCounter", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansCounter"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CleansCounter["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansCounter", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansCounter"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CleansCounter["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansCounter", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansCounter"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CleansCounter["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansCounter", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansCounter"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CleansCounter}
                      value={data.CleansCounter["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Wipes appliances
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WipesAppliances", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WipesAppliances"
                      options={[{ _id: "1", name: "" }]}
                      value={data.WipesAppliances["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WipesAppliances", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WipesAppliances"
                      options={[{ _id: "2", name: "" }]}
                      value={data.WipesAppliances["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WipesAppliances", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WipesAppliances"
                      options={[{ _id: "3", name: "" }]}
                      value={data.WipesAppliances["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WipesAppliances", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WipesAppliances"
                      options={[{ _id: "4", name: "" }]}
                      value={data.WipesAppliances["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WipesAppliances", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WipesAppliances"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.WipesAppliances}
                      value={data.WipesAppliances["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Cleans oven
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansOven", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansOven"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CleansOven["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansOven", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansOven"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CleansOven["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansOven", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansOven"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CleansOven["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansOven", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansOven"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CleansOven["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CleansOven", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansOven"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CleansOven}
                      value={data.CleansOven["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Cleans refrigerator
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "CleansRefrigerator",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansRefrigerator"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CleansRefrigerator["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "CleansRefrigerator",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansRefrigerator"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CleansRefrigerator["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "CleansRefrigerator",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansRefrigerator"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CleansRefrigerator["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "CleansRefrigerator",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansRefrigerator"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CleansRefrigerator["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "CleansRefrigerator",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="CleansRefrigerator"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CleansRefrigerator}
                      value={data.CleansRefrigerator["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Sets table correctly
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTable", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTable"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SetsTable["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTable", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTable"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SetsTable["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTable", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTable"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SetsTable["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTable", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTable"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SetsTable["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTable", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTable"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SetsTable}
                      value={data.SetsTable["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Operates garbage disposal
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OperatesDisposal", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OperatesDisposal"
                      options={[{ _id: "1", name: "" }]}
                      value={data.OperatesDisposal["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OperatesDisposal", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OperatesDisposal"
                      options={[{ _id: "2", name: "" }]}
                      value={data.OperatesDisposal["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OperatesDisposal", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OperatesDisposal"
                      options={[{ _id: "3", name: "" }]}
                      value={data.OperatesDisposal["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OperatesDisposal", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OperatesDisposal"
                      options={[{ _id: "4", name: "" }]}
                      value={data.OperatesDisposal["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OperatesDisposal", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OperatesDisposal"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.OperatesDisposal}
                      value={data.OperatesDisposal["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Educational Skills</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Reads own first and last name
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsName", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsName"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ReadsName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsName", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsName"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ReadsName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsName", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsName"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ReadsName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsName", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsName"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ReadsName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsName", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsName"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ReadsName}
                      value={data.ReadsName["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Reads and understands meaning of survival words
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "ReadsSurvivalWords",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsSurvivalWords"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ReadsSurvivalWords["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "ReadsSurvivalWords",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsSurvivalWords"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ReadsSurvivalWords["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "ReadsSurvivalWords",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsSurvivalWords"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ReadsSurvivalWords["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "ReadsSurvivalWords",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsSurvivalWords"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ReadsSurvivalWords["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "ReadsSurvivalWords",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsSurvivalWords"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ReadsSurvivalWords}
                      value={data.ReadsSurvivalWords["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Reads simple words
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsSimpleWords", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsSimpleWords"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ReadsSimpleWords["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsSimpleWords", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsSimpleWords"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ReadsSimpleWords["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsSimpleWords", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsSimpleWords"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ReadsSimpleWords["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsSimpleWords", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsSimpleWords"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ReadsSimpleWords["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsSimpleWords", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsSimpleWords"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ReadsSimpleWords}
                      value={data.ReadsSimpleWords["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Reads stories, books, etc.
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsStories", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsStories"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ReadsStories["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsStories", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsStories"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ReadsStories["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsStories", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsStories"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ReadsStories["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsStories", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsStories"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ReadsStories["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsStories", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsStories"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ReadsStories}
                      value={data.ReadsStories["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Alphabetizes
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Alphabetizes", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Alphabetizes"
                      options={[{ _id: "1", name: "" }]}
                      value={data.Alphabetizes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Alphabetizes", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Alphabetizes"
                      options={[{ _id: "2", name: "" }]}
                      value={data.Alphabetizes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Alphabetizes", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Alphabetizes"
                      options={[{ _id: "3", name: "" }]}
                      value={data.Alphabetizes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Alphabetizes", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Alphabetizes"
                      options={[{ _id: "4", name: "" }]}
                      value={data.Alphabetizes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Alphabetizes", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Alphabetizes"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.Alphabetizes}
                      value={data.Alphabetizes["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses dictionary
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesDictionary", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesDictionary"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesDictionary["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesDictionary", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesDictionary"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesDictionary["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesDictionary", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesDictionary"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesDictionary["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesDictionary", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesDictionary"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesDictionary["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesDictionary", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesDictionary"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesDictionary}
                      value={data.UsesDictionary["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Identifies letters of the alphabet
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "IdentifiesLetters",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesLetters"
                      options={[{ _id: "1", name: "" }]}
                      value={data.IdentifiesLetters["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "IdentifiesLetters",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesLetters"
                      options={[{ _id: "2", name: "" }]}
                      value={data.IdentifiesLetters["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "IdentifiesLetters",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesLetters"
                      options={[{ _id: "3", name: "" }]}
                      value={data.IdentifiesLetters["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "IdentifiesLetters",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesLetters"
                      options={[{ _id: "4", name: "" }]}
                      value={data.IdentifiesLetters["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "IdentifiesLetters",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesLetters"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.IdentifiesLetters}
                      value={data.IdentifiesLetters["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Prints letters of alphabet
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsLetters", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsLetters"
                      options={[{ _id: "1", name: "" }]}
                      value={data.PrintsLetters["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsLetters", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsLetters"
                      options={[{ _id: "2", name: "" }]}
                      value={data.PrintsLetters["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsLetters", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsLetters"
                      options={[{ _id: "3", name: "" }]}
                      value={data.PrintsLetters["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsLetters", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsLetters"
                      options={[{ _id: "4", name: "" }]}
                      value={data.PrintsLetters["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsLetters", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsLetters"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.PrintsLetters}
                      value={data.PrintsLetters["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Prints name
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsName", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsName"
                      options={[{ _id: "1", name: "" }]}
                      value={data.PrintsName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsName", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsName"
                      options={[{ _id: "2", name: "" }]}
                      value={data.PrintsName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsName", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsName"
                      options={[{ _id: "3", name: "" }]}
                      value={data.PrintsName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsName", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsName"
                      options={[{ _id: "4", name: "" }]}
                      value={data.PrintsName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsName", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsName"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.PrintsName}
                      value={data.PrintsName["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Prints address
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsAddress", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsAddress"
                      options={[{ _id: "1", name: "" }]}
                      value={data.PrintsAddress["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsAddress", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsAddress"
                      options={[{ _id: "2", name: "" }]}
                      value={data.PrintsAddress["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsAddress", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsAddress"
                      options={[{ _id: "3", name: "" }]}
                      value={data.PrintsAddress["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsAddress", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsAddress"
                      options={[{ _id: "4", name: "" }]}
                      value={data.PrintsAddress["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsAddress", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsAddress"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.PrintsAddress}
                      value={data.PrintsAddress["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Prints simple words
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "PrintsSimpleWords",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsSimpleWords"
                      options={[{ _id: "1", name: "" }]}
                      value={data.PrintsSimpleWords["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "PrintsSimpleWords",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsSimpleWords"
                      options={[{ _id: "2", name: "" }]}
                      value={data.PrintsSimpleWords["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "PrintsSimpleWords",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsSimpleWords"
                      options={[{ _id: "3", name: "" }]}
                      value={data.PrintsSimpleWords["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "PrintsSimpleWords",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsSimpleWords"
                      options={[{ _id: "4", name: "" }]}
                      value={data.PrintsSimpleWords["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "PrintsSimpleWords",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsSimpleWords"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.PrintsSimpleWords}
                      value={data.PrintsSimpleWords["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Prints sentences
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsSentences", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsSentences"
                      options={[{ _id: "1", name: "" }]}
                      value={data.PrintsSentences["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsSentences", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsSentences"
                      options={[{ _id: "2", name: "" }]}
                      value={data.PrintsSentences["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsSentences", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsSentences"
                      options={[{ _id: "3", name: "" }]}
                      value={data.PrintsSentences["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsSentences", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsSentences"
                      options={[{ _id: "4", name: "" }]}
                      value={data.PrintsSentences["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PrintsSentences", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PrintsSentences"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.PrintsSentences}
                      value={data.PrintsSentences["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Composes a letter
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ComposesLetter", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ComposesLetter"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ComposesLetter["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ComposesLetter", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ComposesLetter"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ComposesLetter["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ComposesLetter", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ComposesLetter"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ComposesLetter["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ComposesLetter", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ComposesLetter"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ComposesLetter["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ComposesLetter", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ComposesLetter"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ComposesLetter}
                      value={data.ComposesLetter["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Addresses an envelope
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AddressesEnvelope",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AddressesEnvelope"
                      options={[{ _id: "1", name: "" }]}
                      value={data.AddressesEnvelope["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AddressesEnvelope",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AddressesEnvelope"
                      options={[{ _id: "2", name: "" }]}
                      value={data.AddressesEnvelope["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AddressesEnvelope",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AddressesEnvelope"
                      options={[{ _id: "3", name: "" }]}
                      value={data.AddressesEnvelope["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AddressesEnvelope",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AddressesEnvelope"
                      options={[{ _id: "4", name: "" }]}
                      value={data.AddressesEnvelope["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AddressesEnvelope",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AddressesEnvelope"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.AddressesEnvelope}
                      value={data.AddressesEnvelope["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Identifies name in printed text
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesName", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesName"
                      options={[{ _id: "1", name: "" }]}
                      value={data.IdentifiesName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesName", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesName"
                      options={[{ _id: "2", name: "" }]}
                      value={data.IdentifiesName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesName", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesName"
                      options={[{ _id: "3", name: "" }]}
                      value={data.IdentifiesName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesName", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesName"
                      options={[{ _id: "4", name: "" }]}
                      value={data.IdentifiesName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesName", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesName"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.IdentifiesName}
                      value={data.IdentifiesName["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Tells time to hour
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TellsTimeHour", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeHour"
                      options={[{ _id: "1", name: "" }]}
                      value={data.TellsTimeHour["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TellsTimeHour", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeHour"
                      options={[{ _id: "2", name: "" }]}
                      value={data.TellsTimeHour["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TellsTimeHour", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeHour"
                      options={[{ _id: "3", name: "" }]}
                      value={data.TellsTimeHour["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TellsTimeHour", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeHour"
                      options={[{ _id: "4", name: "" }]}
                      value={data.TellsTimeHour["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TellsTimeHour", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeHour"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.TellsTimeHour}
                      value={data.TellsTimeHour["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Tells time to half hour
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "TellsTimeHalfHour",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeHalfHour"
                      options={[{ _id: "1", name: "" }]}
                      value={data.TellsTimeHalfHour["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "TellsTimeHalfHour",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeHalfHour"
                      options={[{ _id: "2", name: "" }]}
                      value={data.TellsTimeHalfHour["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "TellsTimeHalfHour",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeHalfHour"
                      options={[{ _id: "3", name: "" }]}
                      value={data.TellsTimeHalfHour["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "TellsTimeHalfHour",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeHalfHour"
                      options={[{ _id: "4", name: "" }]}
                      value={data.TellsTimeHalfHour["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "TellsTimeHalfHour",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeHalfHour"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.TellsTimeHalfHour}
                      value={data.TellsTimeHalfHour["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Tells time to five minute intervals
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "TellsTimeFiveMinutes",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeFiveMinutes"
                      options={[{ _id: "1", name: "" }]}
                      value={data.TellsTimeFiveMinutes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "TellsTimeFiveMinutes",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeFiveMinutes"
                      options={[{ _id: "2", name: "" }]}
                      value={data.TellsTimeFiveMinutes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "TellsTimeFiveMinutes",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeFiveMinutes"
                      options={[{ _id: "3", name: "" }]}
                      value={data.TellsTimeFiveMinutes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "TellsTimeFiveMinutes",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeFiveMinutes"
                      options={[{ _id: "4", name: "" }]}
                      value={data.TellsTimeFiveMinutes["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "TellsTimeFiveMinutes",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="TellsTimeFiveMinutes"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.TellsTimeFiveMinutes}
                      value={data.TellsTimeFiveMinutes["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Sets time on clocks
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTime", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTime"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SetsTime["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTime", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTime"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SetsTime["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTime", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTime"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SetsTime["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTime", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTime"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SetsTime["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTime", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTime"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SetsTime}
                      value={data.SetsTime["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Reads numbers 1 – 31
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsNumbers", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsNumbers"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ReadsNumbers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsNumbers", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsNumbers"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ReadsNumbers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsNumbers", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsNumbers"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ReadsNumbers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsNumbers", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsNumbers"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ReadsNumbers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsNumbers", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsNumbers"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ReadsNumbers}
                      value={data.ReadsNumbers["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Names days of week
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesDays", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesDays"
                      options={[{ _id: "1", name: "" }]}
                      value={data.NamesDays["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesDays", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesDays"
                      options={[{ _id: "2", name: "" }]}
                      value={data.NamesDays["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesDays", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesDays"
                      options={[{ _id: "3", name: "" }]}
                      value={data.NamesDays["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesDays", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesDays"
                      options={[{ _id: "4", name: "" }]}
                      value={data.NamesDays["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesDays", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesDays"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.NamesDays}
                      value={data.NamesDays["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Names present day of week
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesPresentDay", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesPresentDay"
                      options={[{ _id: "1", name: "" }]}
                      value={data.NamesPresentDay["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesPresentDay", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesPresentDay"
                      options={[{ _id: "2", name: "" }]}
                      value={data.NamesPresentDay["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesPresentDay", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesPresentDay"
                      options={[{ _id: "3", name: "" }]}
                      value={data.NamesPresentDay["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesPresentDay", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesPresentDay"
                      options={[{ _id: "4", name: "" }]}
                      value={data.NamesPresentDay["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesPresentDay", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesPresentDay"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.NamesPresentDay}
                      value={data.NamesPresentDay["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Names present month
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "NamesPresentMonth",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesPresentMonth"
                      options={[{ _id: "1", name: "" }]}
                      value={data.NamesPresentMonth["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "NamesPresentMonth",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesPresentMonth"
                      options={[{ _id: "2", name: "" }]}
                      value={data.NamesPresentMonth["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "NamesPresentMonth",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesPresentMonth"
                      options={[{ _id: "3", name: "" }]}
                      value={data.NamesPresentMonth["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "NamesPresentMonth",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesPresentMonth"
                      options={[{ _id: "4", name: "" }]}
                      value={data.NamesPresentMonth["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "NamesPresentMonth",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesPresentMonth"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.NamesPresentMonth}
                      value={data.NamesPresentMonth["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Names seasons
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesSeasons", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesSeasons"
                      options={[{ _id: "1", name: "" }]}
                      value={data.NamesSeasons["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesSeasons", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesSeasons"
                      options={[{ _id: "2", name: "" }]}
                      value={data.NamesSeasons["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesSeasons", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesSeasons"
                      options={[{ _id: "3", name: "" }]}
                      value={data.NamesSeasons["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesSeasons", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesSeasons"
                      options={[{ _id: "4", name: "" }]}
                      value={data.NamesSeasons["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "NamesSeasons", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamesSeasons"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.NamesSeasons}
                      value={data.NamesSeasons["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Names present season
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "NamePresentSeason",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamePresentSeason"
                      options={[{ _id: "1", name: "" }]}
                      value={data.NamePresentSeason["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "NamePresentSeason",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamePresentSeason"
                      options={[{ _id: "2", name: "" }]}
                      value={data.NamePresentSeason["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "NamePresentSeason",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamePresentSeason"
                      options={[{ _id: "3", name: "" }]}
                      value={data.NamePresentSeason["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "NamePresentSeason",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamePresentSeason"
                      options={[{ _id: "4", name: "" }]}
                      value={data.NamePresentSeason["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "NamePresentSeason",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="NamePresentSeason"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.NamePresentSeason}
                      value={data.NamePresentSeason["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Identifies months with holidays
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesMonths", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesMonths"
                      options={[{ _id: "1", name: "" }]}
                      value={data.IdentifiesMonths["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesMonths", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesMonths"
                      options={[{ _id: "2", name: "" }]}
                      value={data.IdentifiesMonths["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesMonths", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesMonths"
                      options={[{ _id: "3", name: "" }]}
                      value={data.IdentifiesMonths["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesMonths", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesMonths"
                      options={[{ _id: "4", name: "" }]}
                      value={data.IdentifiesMonths["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesMonths", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesMonths"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.IdentifiesMonths}
                      value={data.IdentifiesMonths["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    States own birthday
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesOwnBirthday",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesOwnBirthday"
                      options={[{ _id: "1", name: "" }]}
                      value={data.StatesOwnBirthday["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesOwnBirthday",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesOwnBirthday"
                      options={[{ _id: "2", name: "" }]}
                      value={data.StatesOwnBirthday["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesOwnBirthday",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesOwnBirthday"
                      options={[{ _id: "3", name: "" }]}
                      value={data.StatesOwnBirthday["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesOwnBirthday",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesOwnBirthday"
                      options={[{ _id: "4", name: "" }]}
                      value={data.StatesOwnBirthday["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesOwnBirthday",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesOwnBirthday"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.StatesOwnBirthday}
                      value={data.StatesOwnBirthday["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Locates dates on calendar
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "LocatesDates", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="LocatesDates"
                      options={[{ _id: "1", name: "" }]}
                      value={data.LocatesDates["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "LocatesDates", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="LocatesDates"
                      options={[{ _id: "2", name: "" }]}
                      value={data.LocatesDates["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "LocatesDates", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="LocatesDates"
                      options={[{ _id: "3", name: "" }]}
                      value={data.LocatesDates["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "LocatesDates", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="LocatesDates"
                      options={[{ _id: "4", name: "" }]}
                      value={data.LocatesDates["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "LocatesDates", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="LocatesDates"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.LocatesDates}
                      value={data.LocatesDates["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    States present year
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesPresentYear",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesPresentYear"
                      options={[{ _id: "1", name: "" }]}
                      value={data.StatesPresentYear["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesPresentYear",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesPresentYear"
                      options={[{ _id: "2", name: "" }]}
                      value={data.StatesPresentYear["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesPresentYear",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesPresentYear"
                      options={[{ _id: "3", name: "" }]}
                      value={data.StatesPresentYear["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesPresentYear",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesPresentYear"
                      options={[{ _id: "4", name: "" }]}
                      value={data.StatesPresentYear["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesPresentYear",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesPresentYear"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.StatesPresentYear}
                      value={data.StatesPresentYear["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    States present date
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesPresentDate",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesPresentDate"
                      options={[{ _id: "1", name: "" }]}
                      value={data.StatesPresentDate["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesPresentDate",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesPresentDate"
                      options={[{ _id: "2", name: "" }]}
                      value={data.StatesPresentDate["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesPresentDate",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesPresentDate"
                      options={[{ _id: "3", name: "" }]}
                      value={data.StatesPresentDate["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesPresentDate",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesPresentDate"
                      options={[{ _id: "4", name: "" }]}
                      value={data.StatesPresentDate["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesPresentDate",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesPresentDate"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.StatesPresentDate}
                      value={data.StatesPresentDate["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses calendar to record appointments
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseCalendar", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseCalendar"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UseCalendar["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseCalendar", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseCalendar"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UseCalendar["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseCalendar", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseCalendar"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UseCalendar["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseCalendar", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseCalendar"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UseCalendar["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseCalendar", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseCalendar"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UseCalendar}
                      value={data.UseCalendar["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Dials a phone number
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DialPhone", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DialPhone"
                      options={[{ _id: "1", name: "" }]}
                      value={data.DialPhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DialPhone", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DialPhone"
                      options={[{ _id: "2", name: "" }]}
                      value={data.DialPhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DialPhone", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DialPhone"
                      options={[{ _id: "3", name: "" }]}
                      value={data.DialPhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DialPhone", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DialPhone"
                      options={[{ _id: "4", name: "" }]}
                      value={data.DialPhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DialPhone", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DialPhone"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.DialPhone}
                      value={data.DialPhone["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Answers phone correctly
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AnswerPhone", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AnswerPhone"
                      options={[{ _id: "1", name: "" }]}
                      value={data.AnswerPhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AnswerPhone", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AnswerPhone"
                      options={[{ _id: "2", name: "" }]}
                      value={data.AnswerPhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AnswerPhone", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AnswerPhone"
                      options={[{ _id: "3", name: "" }]}
                      value={data.AnswerPhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AnswerPhone", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AnswerPhone"
                      options={[{ _id: "4", name: "" }]}
                      value={data.AnswerPhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AnswerPhone", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AnswerPhone"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.AnswerPhone}
                      value={data.AnswerPhone["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Takes simple messages
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TakeMessages", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TakeMessages"
                      options={[{ _id: "1", name: "" }]}
                      value={data.TakeMessages["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TakeMessages", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TakeMessages"
                      options={[{ _id: "2", name: "" }]}
                      value={data.TakeMessages["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TakeMessages", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TakeMessages"
                      options={[{ _id: "3", name: "" }]}
                      value={data.TakeMessages["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TakeMessages", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TakeMessages"
                      options={[{ _id: "4", name: "" }]}
                      value={data.TakeMessages["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TakeMessages", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TakeMessages"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.TakeMessages}
                      value={data.TakeMessages["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Recites own phone number
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "RecitePhoneNumber",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="RecitePhoneNumber"
                      options={[{ _id: "1", name: "" }]}
                      value={data.RecitePhoneNumber["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "RecitePhoneNumber",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="RecitePhoneNumber"
                      options={[{ _id: "2", name: "" }]}
                      value={data.RecitePhoneNumber["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "RecitePhoneNumber",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="RecitePhoneNumber"
                      options={[{ _id: "3", name: "" }]}
                      value={data.RecitePhoneNumber["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "RecitePhoneNumber",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="RecitePhoneNumber"
                      options={[{ _id: "4", name: "" }]}
                      value={data.RecitePhoneNumber["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "RecitePhoneNumber",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="RecitePhoneNumber"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.RecitePhoneNumber}
                      value={data.RecitePhoneNumber["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Recognizes busy signal
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RecognizeBusy", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RecognizeBusy"
                      options={[{ _id: "1", name: "" }]}
                      value={data.RecognizeBusy["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RecognizeBusy", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RecognizeBusy"
                      options={[{ _id: "2", name: "" }]}
                      value={data.RecognizeBusy["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RecognizeBusy", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RecognizeBusy"
                      options={[{ _id: "3", name: "" }]}
                      value={data.RecognizeBusy["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RecognizeBusy", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RecognizeBusy"
                      options={[{ _id: "4", name: "" }]}
                      value={data.RecognizeBusy["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RecognizeBusy", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RecognizeBusy"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.RecognizeBusy}
                      value={data.RecognizeBusy["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses pay phone
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsePayPhone", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsePayPhone"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsePayPhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsePayPhone", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsePayPhone"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsePayPhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsePayPhone", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsePayPhone"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsePayPhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsePayPhone", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsePayPhone"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsePayPhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsePayPhone", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsePayPhone"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsePayPhone}
                      value={data.UsePayPhone["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Knows how and when to dial 0
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DialZero", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DialZero"
                      options={[{ _id: "1", name: "" }]}
                      value={data.DialZero["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DialZero", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DialZero"
                      options={[{ _id: "2", name: "" }]}
                      value={data.DialZero["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DialZero", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DialZero"
                      options={[{ _id: "3", name: "" }]}
                      value={data.DialZero["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DialZero", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DialZero"
                      options={[{ _id: "4", name: "" }]}
                      value={data.DialZero["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DialZero", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DialZero"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.DialZero}
                      value={data.DialZero["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Verbalizes how and when to make a collect call
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizesCollectCall",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesCollectCall"
                      options={[{ _id: "1", name: "" }]}
                      value={data.VerbalizesCollectCall["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizesCollectCall",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesCollectCall"
                      options={[{ _id: "2", name: "" }]}
                      value={data.VerbalizesCollectCall["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizesCollectCall",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesCollectCall"
                      options={[{ _id: "3", name: "" }]}
                      value={data.VerbalizesCollectCall["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizesCollectCall",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesCollectCall"
                      options={[{ _id: "4", name: "" }]}
                      value={data.VerbalizesCollectCall["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "VerbalizesCollectCall",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesCollectCall"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.VerbalizesCollectCall}
                      value={data.VerbalizesCollectCall["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses phone to make appointments
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsePhone", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsePhone"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsePhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsePhone", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsePhone"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsePhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsePhone", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsePhone"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsePhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsePhone", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsePhone"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsePhone["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsePhone", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsePhone"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsePhone}
                      value={data.UsePhone["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Transportation</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Mobility/ambulatory
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Mobility", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Mobility"
                      options={[{ _id: "1", name: "" }]}
                      value={data.Mobility["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Mobility", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Mobility"
                      options={[{ _id: "2", name: "" }]}
                      value={data.Mobility["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Mobility", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Mobility"
                      options={[{ _id: "3", name: "" }]}
                      value={data.Mobility["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Mobility", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Mobility"
                      options={[{ _id: "4", name: "" }]}
                      value={data.Mobility["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Mobility", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Mobility"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.Mobility}
                      value={data.Mobility["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Crosses street safely
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CrossesStreet", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CrossesStreet"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CrossesStreet["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CrossesStreet", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CrossesStreet"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CrossesStreet["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CrossesStreet", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CrossesStreet"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CrossesStreet["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CrossesStreet", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CrossesStreet"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CrossesStreet["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CrossesStreet", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CrossesStreet"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CrossesStreet}
                      value={data.CrossesStreet["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses traffic light
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesTrafficLight", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTrafficLight"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesTrafficLight["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesTrafficLight", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTrafficLight"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesTrafficLight["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesTrafficLight", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTrafficLight"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesTrafficLight["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesTrafficLight", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTrafficLight"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesTrafficLight["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesTrafficLight", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTrafficLight"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesTrafficLight}
                      value={data.UsesTrafficLight["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Can find way around neighborhood
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FindWay", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FindWay"
                      options={[{ _id: "1", name: "" }]}
                      value={data.FindWay["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FindWay", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FindWay"
                      options={[{ _id: "2", name: "" }]}
                      value={data.FindWay["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FindWay", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FindWay"
                      options={[{ _id: "3", name: "" }]}
                      value={data.FindWay["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FindWay", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FindWay"
                      options={[{ _id: "4", name: "" }]}
                      value={data.FindWay["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FindWay", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FindWay"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.FindWay}
                      value={data.FindWay["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Rides a bike
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RidesBike", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RidesBike"
                      options={[{ _id: "1", name: "" }]}
                      value={data.RidesBike["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RidesBike", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RidesBike"
                      options={[{ _id: "2", name: "" }]}
                      value={data.RidesBike["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RidesBike", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RidesBike"
                      options={[{ _id: "3", name: "" }]}
                      value={data.RidesBike["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RidesBike", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RidesBike"
                      options={[{ _id: "4", name: "" }]}
                      value={data.RidesBike["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RidesBike", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RidesBike"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.RidesBike}
                      value={data.RidesBike["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses bus system
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesBus", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesBus"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesBus["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesBus", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesBus"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesBus["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesBus", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesBus"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesBus["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesBus", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesBus"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesBus["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesBus", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesBus"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesBus}
                      value={data.UsesBus["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses taxi
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesTaxi", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTaxi"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesTaxi["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesTaxi", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTaxi"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesTaxi["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesTaxi", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTaxi"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesTaxi["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesTaxi", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTaxi"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesTaxi["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesTaxi", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTaxi"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesTaxi}
                      value={data.UsesTaxi["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses ID card
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesID", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesID"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesID["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesID", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesID"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesID["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesID", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesID"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesID["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesID", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesID"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesID["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesID", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesID"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesID}
                      value={data.UsesID["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Is appropriate with strangers
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateWithStrangers",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateWithStrangers"
                      options={[{ _id: "1", name: "" }]}
                      value={data.AppropriateWithStrangers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateWithStrangers",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateWithStrangers"
                      options={[{ _id: "2", name: "" }]}
                      value={data.AppropriateWithStrangers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateWithStrangers",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateWithStrangers"
                      options={[{ _id: "3", name: "" }]}
                      value={data.AppropriateWithStrangers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateWithStrangers",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateWithStrangers"
                      options={[{ _id: "4", name: "" }]}
                      value={data.AppropriateWithStrangers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateWithStrangers",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateWithStrangers"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.AppropriateWithStrangers}
                      value={data.AppropriateWithStrangers["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Drives a car
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DrivesCar", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DrivesCar"
                      options={[{ _id: "1", name: "" }]}
                      value={data.DrivesCar["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DrivesCar", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DrivesCar"
                      options={[{ _id: "2", name: "" }]}
                      value={data.DrivesCar["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DrivesCar", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DrivesCar"
                      options={[{ _id: "3", name: "" }]}
                      value={data.DrivesCar["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DrivesCar", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DrivesCar"
                      options={[{ _id: "4", name: "" }]}
                      value={data.DrivesCar["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DrivesCar", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DrivesCar"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.DrivesCar}
                      value={data.DrivesCar["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Knows basic safety signs
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsSafety", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsSafety"
                      options={[{ _id: "1", name: "" }]}
                      value={data.KnowsSafety["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsSafety", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsSafety"
                      options={[{ _id: "2", name: "" }]}
                      value={data.KnowsSafety["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsSafety", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsSafety"
                      options={[{ _id: "3", name: "" }]}
                      value={data.KnowsSafety["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsSafety", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsSafety"
                      options={[{ _id: "4", name: "" }]}
                      value={data.KnowsSafety["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsSafety", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsSafety"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.KnowsSafety}
                      value={data.KnowsSafety["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Housing Skills</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Changes light bulbs
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ChangesBulb", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ChangesBulb"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ChangesBulb["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ChangesBulb", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ChangesBulb"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ChangesBulb["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ChangesBulb", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ChangesBulb"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ChangesBulb["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ChangesBulb", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ChangesBulb"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ChangesBulb["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ChangesBulb", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ChangesBulb"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ChangesBulb}
                      value={data.ChangesBulb["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Reports repairs as needed
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReportRepairs", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReportRepairs"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ReportRepairs["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReportRepairs", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReportRepairs"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ReportRepairs["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReportRepairs", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReportRepairs"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ReportRepairs["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReportRepairs", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReportRepairs"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ReportRepairs["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReportRepairs", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReportRepairs"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ReportRepairs}
                      value={data.ReportRepairs["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Knows pest control
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PestControl", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PestControl"
                      options={[{ _id: "1", name: "" }]}
                      value={data.PestControl["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PestControl", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PestControl"
                      options={[{ _id: "2", name: "" }]}
                      value={data.PestControl["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PestControl", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PestControl"
                      options={[{ _id: "3", name: "" }]}
                      value={data.PestControl["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PestControl", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PestControl"
                      options={[{ _id: "4", name: "" }]}
                      value={data.PestControl["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PestControl", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PestControl"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.PestControl}
                      value={data.PestControl["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses rake, hoe, etc.
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesRake", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesRake"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesRake["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesRake", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesRake"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesRake["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesRake", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesRake"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesRake["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesRake", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesRake"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesRake["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesRake", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesRake"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesRake}
                      value={data.UsesRake["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses lawn mower
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMower", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMower"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesMower["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMower", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMower"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesMower["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMower", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMower"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesMower["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMower", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMower"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesMower["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMower", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMower"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesMower}
                      value={data.UsesMower["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Waters lawn
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WatersLawn", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WatersLawn"
                      options={[{ _id: "1", name: "" }]}
                      value={data.WatersLawn["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WatersLawn", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WatersLawn"
                      options={[{ _id: "2", name: "" }]}
                      value={data.WatersLawn["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WatersLawn", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WatersLawn"
                      options={[{ _id: "3", name: "" }]}
                      value={data.WatersLawn["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WatersLawn", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WatersLawn"
                      options={[{ _id: "4", name: "" }]}
                      value={data.WatersLawn["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WatersLawn", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WatersLawn"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.WatersLawn}
                      value={data.WatersLawn["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Waters flowers
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WatersFlowers", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WatersFlowers"
                      options={[{ _id: "1", name: "" }]}
                      value={data.WatersFlowers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WatersFlowers", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WatersFlowers"
                      options={[{ _id: "2", name: "" }]}
                      value={data.WatersFlowers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WatersFlowers", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WatersFlowers"
                      options={[{ _id: "3", name: "" }]}
                      value={data.WatersFlowers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WatersFlowers", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WatersFlowers"
                      options={[{ _id: "4", name: "" }]}
                      value={data.WatersFlowers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WatersFlowers", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WatersFlowers"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.WatersFlowers}
                      value={data.WatersFlowers["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Has realistic plan for housing
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RealisticPlan", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RealisticPlan"
                      options={[{ _id: "1", name: "" }]}
                      value={data.RealisticPlan["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RealisticPlan", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RealisticPlan"
                      options={[{ _id: "2", name: "" }]}
                      value={data.RealisticPlan["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RealisticPlan", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RealisticPlan"
                      options={[{ _id: "3", name: "" }]}
                      value={data.RealisticPlan["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RealisticPlan", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RealisticPlan"
                      options={[{ _id: "4", name: "" }]}
                      value={data.RealisticPlan["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "RealisticPlan", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="RealisticPlan"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.RealisticPlan}
                      value={data.RealisticPlan["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Food Management</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Makes shopping list
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ShoppingList", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ShoppingList"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ShoppingList["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ShoppingList", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ShoppingList"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ShoppingList["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ShoppingList", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ShoppingList"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ShoppingList["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ShoppingList", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ShoppingList"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ShoppingList["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ShoppingList", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ShoppingList"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ShoppingList}
                      value={data.ShoppingList["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Finds items in orderly manner
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FindItems", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FindItems"
                      options={[{ _id: "1", name: "" }]}
                      value={data.FindItems["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FindItems", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FindItems"
                      options={[{ _id: "2", name: "" }]}
                      value={data.FindItems["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FindItems", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FindItems"
                      options={[{ _id: "3", name: "" }]}
                      value={data.FindItems["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FindItems", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FindItems"
                      options={[{ _id: "4", name: "" }]}
                      value={data.FindItems["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FindItems", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FindItems"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.FindItems}
                      value={data.FindItems["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Compares prices
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ComparePrices", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ComparePrices"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ComparePrices["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ComparePrices", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ComparePrices"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ComparePrices["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ComparePrices", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ComparePrices"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ComparePrices["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ComparePrices", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ComparePrices"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ComparePrices["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ComparePrices", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ComparePrices"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ComparePrices}
                      value={data.ComparePrices["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Adds items on calculator
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AddItems", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AddItems"
                      options={[{ _id: "1", name: "" }]}
                      value={data.AddItems["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AddItems", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AddItems"
                      options={[{ _id: "2", name: "" }]}
                      value={data.AddItems["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AddItems", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AddItems"
                      options={[{ _id: "3", name: "" }]}
                      value={data.AddItems["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AddItems", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AddItems"
                      options={[{ _id: "4", name: "" }]}
                      value={data.AddItems["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AddItems", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AddItems"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.AddItems}
                      value={data.AddItems["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses coupons
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseCoupons", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseCoupons"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UseCoupons["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseCoupons", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseCoupons"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UseCoupons["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseCoupons", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseCoupons"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UseCoupons["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseCoupons", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseCoupons"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UseCoupons["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseCoupons", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseCoupons"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UseCoupons}
                      value={data.UseCoupons["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses food stamps
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseFoodStamps", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseFoodStamps"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UseFoodStamps["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseFoodStamps", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseFoodStamps"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UseFoodStamps["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseFoodStamps", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseFoodStamps"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UseFoodStamps["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseFoodStamps", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseFoodStamps"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UseFoodStamps["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UseFoodStamps", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UseFoodStamps"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UseFoodStamps}
                      value={data.UseFoodStamps["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Properly stores groceries
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StoresGroceries", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StoresGroceries"
                      options={[{ _id: "1", name: "" }]}
                      value={data.StoresGroceries["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StoresGroceries", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StoresGroceries"
                      options={[{ _id: "2", name: "" }]}
                      value={data.StoresGroceries["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StoresGroceries", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StoresGroceries"
                      options={[{ _id: "3", name: "" }]}
                      value={data.StoresGroceries["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StoresGroceries", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StoresGroceries"
                      options={[{ _id: "4", name: "" }]}
                      value={data.StoresGroceries["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StoresGroceries", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StoresGroceries"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.StoresGroceries}
                      value={data.StoresGroceries["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Stays in budget
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StaysInBudget", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StaysInBudget"
                      options={[{ _id: "1", name: "" }]}
                      value={data.StaysInBudget["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StaysInBudget", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StaysInBudget"
                      options={[{ _id: "2", name: "" }]}
                      value={data.StaysInBudget["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StaysInBudget", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StaysInBudget"
                      options={[{ _id: "3", name: "" }]}
                      value={data.StaysInBudget["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StaysInBudget", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StaysInBudget"
                      options={[{ _id: "4", name: "" }]}
                      value={data.StaysInBudget["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StaysInBudget", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StaysInBudget"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.StaysInBudget}
                      value={data.StaysInBudget["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Buys appropriate groceries
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateGroceries",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateGroceries"
                      options={[{ _id: "1", name: "" }]}
                      value={data.AppropriateGroceries["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateGroceries",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateGroceries"
                      options={[{ _id: "2", name: "" }]}
                      value={data.AppropriateGroceries["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateGroceries",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateGroceries"
                      options={[{ _id: "3", name: "" }]}
                      value={data.AppropriateGroceries["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateGroceries",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateGroceries"
                      options={[{ _id: "4", name: "" }]}
                      value={data.AppropriateGroceries["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateGroceries",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateGroceries"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.AppropriateGroceries}
                      value={data.AppropriateGroceries["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Knows food groups
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FoodGroups", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FoodGroups"
                      options={[{ _id: "1", name: "" }]}
                      value={data.FoodGroups["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FoodGroups", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FoodGroups"
                      options={[{ _id: "2", name: "" }]}
                      value={data.FoodGroups["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FoodGroups", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FoodGroups"
                      options={[{ _id: "3", name: "" }]}
                      value={data.FoodGroups["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FoodGroups", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FoodGroups"
                      options={[{ _id: "4", name: "" }]}
                      value={data.FoodGroups["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FoodGroups", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FoodGroups"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.FoodGroups}
                      value={data.FoodGroups["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Plans simple meal
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PlansSimpleMeal", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansSimpleMeal"
                      options={[{ _id: "1", name: "" }]}
                      value={data.PlansSimpleMeal["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PlansSimpleMeal", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansSimpleMeal"
                      options={[{ _id: "2", name: "" }]}
                      value={data.PlansSimpleMeal["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PlansSimpleMeal", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansSimpleMeal"
                      options={[{ _id: "3", name: "" }]}
                      value={data.PlansSimpleMeal["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PlansSimpleMeal", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansSimpleMeal"
                      options={[{ _id: "4", name: "" }]}
                      value={data.PlansSimpleMeal["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PlansSimpleMeal", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansSimpleMeal"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.PlansSimpleMeal}
                      value={data.PlansSimpleMeal["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Plans meals for one day
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PlansMealsForDay", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansMealsForDay"
                      options={[{ _id: "1", name: "" }]}
                      value={data.PlansMealsForDay["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PlansMealsForDay", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansMealsForDay"
                      options={[{ _id: "2", name: "" }]}
                      value={data.PlansMealsForDay["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PlansMealsForDay", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansMealsForDay"
                      options={[{ _id: "3", name: "" }]}
                      value={data.PlansMealsForDay["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PlansMealsForDay", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansMealsForDay"
                      options={[{ _id: "4", name: "" }]}
                      value={data.PlansMealsForDay["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PlansMealsForDay", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansMealsForDay"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.PlansMealsForDay}
                      value={data.PlansMealsForDay["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Plans meals for one week
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "PlansMealsForWeek",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansMealsForWeek"
                      options={[{ _id: "1", name: "" }]}
                      value={data.PlansMealsForWeek["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "PlansMealsForWeek",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansMealsForWeek"
                      options={[{ _id: "2", name: "" }]}
                      value={data.PlansMealsForWeek["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "PlansMealsForWeek",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansMealsForWeek"
                      options={[{ _id: "3", name: "" }]}
                      value={data.PlansMealsForWeek["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "PlansMealsForWeek",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansMealsForWeek"
                      options={[{ _id: "4", name: "" }]}
                      value={data.PlansMealsForWeek["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "PlansMealsForWeek",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="PlansMealsForWeek"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.PlansMealsForWeek}
                      value={data.PlansMealsForWeek["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    States order of preparing food
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesFoodPrepOrder",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesFoodPrepOrder"
                      options={[{ _id: "1", name: "" }]}
                      value={data.StatesFoodPrepOrder["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesFoodPrepOrder",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesFoodPrepOrder"
                      options={[{ _id: "2", name: "" }]}
                      value={data.StatesFoodPrepOrder["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesFoodPrepOrder",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesFoodPrepOrder"
                      options={[{ _id: "3", name: "" }]}
                      value={data.StatesFoodPrepOrder["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesFoodPrepOrder",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesFoodPrepOrder"
                      options={[{ _id: "4", name: "" }]}
                      value={data.StatesFoodPrepOrder["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "StatesFoodPrepOrder",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="StatesFoodPrepOrder"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.StatesFoodPrepOrder}
                      value={data.StatesFoodPrepOrder["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    StatesFoodPrepOrder
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsOvenTemp", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsOvenTemp"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SetsOvenTemp["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsOvenTemp", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsOvenTemp"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SetsOvenTemp["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsOvenTemp", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsOvenTemp"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SetsOvenTemp["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsOvenTemp", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsOvenTemp"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SetsOvenTemp["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsOvenTemp", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsOvenTemp"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SetsOvenTemp}
                      value={data.SetsOvenTemp["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Sets burner on stove
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsBurner", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsBurner"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SetsBurner["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsBurner", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsBurner"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SetsBurner["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsBurner", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsBurner"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SetsBurner["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsBurner", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsBurner"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SetsBurner["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsBurner", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsBurner"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SetsBurner}
                      value={data.SetsBurner["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Follows recipe
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FollowsRecipe", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowsRecipe"
                      options={[{ _id: "1", name: "" }]}
                      value={data.FollowsRecipe["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FollowsRecipe", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowsRecipe"
                      options={[{ _id: "2", name: "" }]}
                      value={data.FollowsRecipe["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FollowsRecipe", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowsRecipe"
                      options={[{ _id: "3", name: "" }]}
                      value={data.FollowsRecipe["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FollowsRecipe", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowsRecipe"
                      options={[{ _id: "4", name: "" }]}
                      value={data.FollowsRecipe["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FollowsRecipe", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowsRecipe"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.FollowsRecipe}
                      value={data.FollowsRecipe["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Sets timer
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTimer", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTimer"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SetsTimer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTimer", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTimer"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SetsTimer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTimer", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTimer"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SetsTimer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTimer", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTimer"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SetsTimer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsTimer", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsTimer"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SetsTimer}
                      value={data.SetsTimer["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Turns off oven/stove
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TurnsOff", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TurnsOff"
                      options={[{ _id: "1", name: "" }]}
                      value={data.TurnsOff["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TurnsOff", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TurnsOff"
                      options={[{ _id: "2", name: "" }]}
                      value={data.TurnsOff["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TurnsOff", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TurnsOff"
                      options={[{ _id: "3", name: "" }]}
                      value={data.TurnsOff["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TurnsOff", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TurnsOff"
                      options={[{ _id: "4", name: "" }]}
                      value={data.TurnsOff["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "TurnsOff", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="TurnsOff"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.TurnsOff}
                      value={data.TurnsOff["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Stores leftovers properly
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StoresLeftovers", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StoresLeftovers"
                      options={[{ _id: "1", name: "" }]}
                      value={data.StoresLeftovers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StoresLeftovers", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StoresLeftovers"
                      options={[{ _id: "2", name: "" }]}
                      value={data.StoresLeftovers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StoresLeftovers", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StoresLeftovers"
                      options={[{ _id: "3", name: "" }]}
                      value={data.StoresLeftovers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StoresLeftovers", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StoresLeftovers"
                      options={[{ _id: "4", name: "" }]}
                      value={data.StoresLeftovers["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StoresLeftovers", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StoresLeftovers"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.StoresLeftovers}
                      value={data.StoresLeftovers["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Makes own breakfast
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesBreakfast", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesBreakfast"
                      options={[{ _id: "1", name: "" }]}
                      value={data.MakesBreakfast["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesBreakfast", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesBreakfast"
                      options={[{ _id: "2", name: "" }]}
                      value={data.MakesBreakfast["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesBreakfast", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesBreakfast"
                      options={[{ _id: "3", name: "" }]}
                      value={data.MakesBreakfast["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesBreakfast", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesBreakfast"
                      options={[{ _id: "4", name: "" }]}
                      value={data.MakesBreakfast["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesBreakfast", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesBreakfast"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.MakesBreakfast}
                      value={data.MakesBreakfast["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Makes own lunch
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesLunch", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesLunch"
                      options={[{ _id: "1", name: "" }]}
                      value={data.MakesLunch["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesLunch", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesLunch"
                      options={[{ _id: "2", name: "" }]}
                      value={data.MakesLunch["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesLunch", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesLunch"
                      options={[{ _id: "3", name: "" }]}
                      value={data.MakesLunch["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesLunch", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesLunch"
                      options={[{ _id: "4", name: "" }]}
                      value={data.MakesLunch["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesLunch", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesLunch"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.MakesLunch}
                      value={data.MakesLunch["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Makes own dinners
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesDinner", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesDinner"
                      options={[{ _id: "1", name: "" }]}
                      value={data.MakesDinner["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesDinner", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesDinner"
                      options={[{ _id: "2", name: "" }]}
                      value={data.MakesDinner["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesDinner", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesDinner"
                      options={[{ _id: "3", name: "" }]}
                      value={data.MakesDinner["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesDinner", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesDinner"
                      options={[{ _id: "4", name: "" }]}
                      value={data.MakesDinner["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesDinner", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesDinner"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.MakesDinner}
                      value={data.MakesDinner["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Makes own snacks
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesSnacks", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesSnacks"
                      options={[{ _id: "1", name: "" }]}
                      value={data.MakesSnacks["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesSnacks", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesSnacks"
                      options={[{ _id: "2", name: "" }]}
                      value={data.MakesSnacks["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesSnacks", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesSnacks"
                      options={[{ _id: "3", name: "" }]}
                      value={data.MakesSnacks["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesSnacks", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesSnacks"
                      options={[{ _id: "4", name: "" }]}
                      value={data.MakesSnacks["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesSnacks", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesSnacks"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.MakesSnacks}
                      value={data.MakesSnacks["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses can opener
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesCanOpener", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesCanOpener"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesCanOpener["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesCanOpener", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesCanOpener"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesCanOpener["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesCanOpener", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesCanOpener"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesCanOpener["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesCanOpener", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesCanOpener"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesCanOpener["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesCanOpener", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesCanOpener"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesCanOpener}
                      value={data.UsesCanOpener["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses mixer
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMixer", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMixer"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesMixer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMixer", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMixer"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesMixer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMixer", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMixer"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesMixer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMixer", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMixer"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesMixer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMixer", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMixer"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesMixer}
                      value={data.UsesMixer["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses toaster
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesToaster", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesToaster"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesToaster["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesToaster", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesToaster"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesToaster["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesToaster", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesToaster"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesToaster["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesToaster", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesToaster"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesToaster["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesToaster", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesToaster"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesToaster}
                      value={data.UsesToaster["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    UsesToaster
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesFoodProcessor",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesFoodProcessor"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesFoodProcessor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesFoodProcessor",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesFoodProcessor"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesFoodProcessor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesFoodProcessor",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesFoodProcessor"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesFoodProcessor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesFoodProcessor",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesFoodProcessor"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesFoodProcessor["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesFoodProcessor",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesFoodProcessor"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesFoodProcessor}
                      value={data.UsesFoodProcessor["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses broiler
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesBroiler", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesBroiler"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesBroiler["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesBroiler", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesBroiler"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesBroiler["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesBroiler", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesBroiler"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesBroiler["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesBroiler", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesBroiler"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesBroiler["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesBroiler", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesBroiler"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesBroiler}
                      value={data.UsesBroiler["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses knife
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesKnife", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesKnife"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesKnife["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesKnife", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesKnife"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesKnife["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesKnife", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesKnife"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesKnife["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesKnife", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesKnife"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesKnife["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesKnife", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesKnife"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesKnife}
                      value={data.UsesKnife["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses grater
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesGrater", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesGrater"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesGrater["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesGrater", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesGrater"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesGrater["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesGrater", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesGrater"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesGrater["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesGrater", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesGrater"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesGrater["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesGrater", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesGrater"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesGrater}
                      value={data.UsesGrater["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses strainer
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesStrainer", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesStrainer"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesStrainer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesStrainer", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesStrainer"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesStrainer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesStrainer", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesStrainer"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesStrainer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesStrainer", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesStrainer"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesStrainer["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesStrainer", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesStrainer"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesStrainer}
                      value={data.UsesStrainer["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses microwave
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMicrowave", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMicrowave"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesMicrowave["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMicrowave", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMicrowave"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesMicrowave["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMicrowave", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMicrowave"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesMicrowave["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMicrowave", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMicrowave"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesMicrowave["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMicrowave", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMicrowave"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesMicrowave}
                      value={data.UsesMicrowave["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses stove
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesStove", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesStove"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesStove["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesStove", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesStove"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesStove["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesStove", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesStove"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesStove["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesStove", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesStove"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesStove["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesStove", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesStove"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesStove}
                      value={data.UsesStove["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses measuring cups and spoons
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesMeasuringCups",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMeasuringCups"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesMeasuringCups["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesMeasuringCups",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMeasuringCups"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesMeasuringCups["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesMeasuringCups",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMeasuringCups"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesMeasuringCups["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesMeasuringCups",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMeasuringCups"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesMeasuringCups["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesMeasuringCups",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMeasuringCups"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesMeasuringCups}
                      value={data.UsesMeasuringCups["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Emergency &amp; Safety Skills</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Aware of home location
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AwareOfHome", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AwareOfHome"
                      options={[{ _id: "1", name: "" }]}
                      value={data.AwareOfHome["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AwareOfHome", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AwareOfHome"
                      options={[{ _id: "2", name: "" }]}
                      value={data.AwareOfHome["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AwareOfHome", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AwareOfHome"
                      options={[{ _id: "3", name: "" }]}
                      value={data.AwareOfHome["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AwareOfHome", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AwareOfHome"
                      options={[{ _id: "4", name: "" }]}
                      value={data.AwareOfHome["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AwareOfHome", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AwareOfHome"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.AwareOfHome}
                      value={data.AwareOfHome["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Oriented to time, place, location
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "OrientedToPresent",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="OrientedToPresent"
                      options={[{ _id: "1", name: "" }]}
                      value={data.OrientedToPresent["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "OrientedToPresent",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="OrientedToPresent"
                      options={[{ _id: "2", name: "" }]}
                      value={data.OrientedToPresent["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "OrientedToPresent",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="OrientedToPresent"
                      options={[{ _id: "3", name: "" }]}
                      value={data.OrientedToPresent["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "OrientedToPresent",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="OrientedToPresent"
                      options={[{ _id: "4", name: "" }]}
                      value={data.OrientedToPresent["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "OrientedToPresent",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="OrientedToPresent"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.OrientedToPresent}
                      value={data.OrientedToPresent["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Knows area or surroundings
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsArea", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsArea"
                      options={[{ _id: "1", name: "" }]}
                      value={data.KnowsArea["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsArea", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsArea"
                      options={[{ _id: "2", name: "" }]}
                      value={data.KnowsArea["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsArea", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsArea"
                      options={[{ _id: "3", name: "" }]}
                      value={data.KnowsArea["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsArea", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsArea"
                      options={[{ _id: "4", name: "" }]}
                      value={data.KnowsArea["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsArea", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsArea"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.KnowsArea}
                      value={data.KnowsArea["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Knows how and when to dial 911
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsEmergency", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsEmergency"
                      options={[{ _id: "1", name: "" }]}
                      value={data.KnowsEmergency["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsEmergency", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsEmergency"
                      options={[{ _id: "2", name: "" }]}
                      value={data.KnowsEmergency["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsEmergency", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsEmergency"
                      options={[{ _id: "3", name: "" }]}
                      value={data.KnowsEmergency["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsEmergency", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsEmergency"
                      options={[{ _id: "4", name: "" }]}
                      value={data.KnowsEmergency["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsEmergency", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsEmergency"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.KnowsEmergency}
                      value={data.KnowsEmergency["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Demonstrates safe use of household appliances
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SafeAppliances", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SafeAppliances"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SafeAppliances["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SafeAppliances", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SafeAppliances"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SafeAppliances["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SafeAppliances", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SafeAppliances"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SafeAppliances["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SafeAppliances", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SafeAppliances"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SafeAppliances["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SafeAppliances", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SafeAppliances"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SafeAppliances}
                      value={data.SafeAppliances["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Verbalizes dangers of certain household substances
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "DangerousSubstances",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="DangerousSubstances"
                      options={[{ _id: "1", name: "" }]}
                      value={data.DangerousSubstances["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "DangerousSubstances",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="DangerousSubstances"
                      options={[{ _id: "2", name: "" }]}
                      value={data.DangerousSubstances["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "DangerousSubstances",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="DangerousSubstances"
                      options={[{ _id: "3", name: "" }]}
                      value={data.DangerousSubstances["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "DangerousSubstances",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="DangerousSubstances"
                      options={[{ _id: "4", name: "" }]}
                      value={data.DangerousSubstances["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "DangerousSubstances",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="DangerousSubstances"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.DangerousSubstances}
                      value={data.DangerousSubstances["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses matches safely
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMatches", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMatches"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesMatches["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMatches", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMatches"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesMatches["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMatches", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMatches"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesMatches["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMatches", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMatches"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesMatches["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesMatches", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesMatches"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesMatches}
                      value={data.UsesMatches["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Exhibits knowledge of fire safety
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FireSafety", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FireSafety"
                      options={[{ _id: "1", name: "" }]}
                      value={data.FireSafety["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FireSafety", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FireSafety"
                      options={[{ _id: "2", name: "" }]}
                      value={data.FireSafety["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FireSafety", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FireSafety"
                      options={[{ _id: "3", name: "" }]}
                      value={data.FireSafety["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FireSafety", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FireSafety"
                      options={[{ _id: "4", name: "" }]}
                      value={data.FireSafety["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "FireSafety", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="FireSafety"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.FireSafety}
                      value={data.FireSafety["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses a fire extinguisher
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesExtinguisher", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesExtinguisher"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesExtinguisher["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesExtinguisher", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesExtinguisher"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesExtinguisher["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesExtinguisher", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesExtinguisher"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesExtinguisher["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesExtinguisher", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesExtinguisher"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesExtinguisher["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "UsesExtinguisher", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesExtinguisher"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesExtinguisher}
                      value={data.UsesExtinguisher["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Checks battery in smoke detector
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ChecksBattery", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ChecksBattery"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ChecksBattery["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ChecksBattery", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ChecksBattery"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ChecksBattery["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ChecksBattery", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ChecksBattery"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ChecksBattery["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ChecksBattery", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ChecksBattery"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ChecksBattery["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ChecksBattery", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ChecksBattery"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ChecksBattery}
                      value={data.ChecksBattery["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Exhibits knowledge about safety and crime
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SafetyCrime", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SafetyCrime"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SafetyCrime["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SafetyCrime", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SafetyCrime"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SafetyCrime["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SafetyCrime", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SafetyCrime"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SafetyCrime["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SafetyCrime", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SafetyCrime"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SafetyCrime["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SafetyCrime", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SafetyCrime"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SafetyCrime}
                      value={data.SafetyCrime["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Knowledge of Community</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses public library
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PublicLibrary", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PublicLibrary"
                      options={[{ _id: "1", name: "" }]}
                      value={data.PublicLibrary["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PublicLibrary", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PublicLibrary"
                      options={[{ _id: "2", name: "" }]}
                      value={data.PublicLibrary["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PublicLibrary", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PublicLibrary"
                      options={[{ _id: "3", name: "" }]}
                      value={data.PublicLibrary["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PublicLibrary", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PublicLibrary"
                      options={[{ _id: "4", name: "" }]}
                      value={data.PublicLibrary["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "PublicLibrary", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="PublicLibrary"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.PublicLibrary}
                      value={data.PublicLibrary["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Able to find community resources
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "CommunityResources",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="CommunityResources"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CommunityResources["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "CommunityResources",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="CommunityResources"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CommunityResources["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "CommunityResources",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="CommunityResources"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CommunityResources["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "CommunityResources",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="CommunityResources"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CommunityResources["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "CommunityResources",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="CommunityResources"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CommunityResources}
                      value={data.CommunityResources["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Uses telephone book
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesTelephoneBook",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTelephoneBook"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UsesTelephoneBook["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesTelephoneBook",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTelephoneBook"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UsesTelephoneBook["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesTelephoneBook",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTelephoneBook"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UsesTelephoneBook["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesTelephoneBook",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTelephoneBook"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UsesTelephoneBook["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UsesTelephoneBook",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UsesTelephoneBook"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UsesTelephoneBook}
                      value={data.UsesTelephoneBook["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Interpersonal Skills</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Communicates needs
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Communicates", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Communicates"
                      options={[{ _id: "1", name: "" }]}
                      value={data.Communicates["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Communicates", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Communicates"
                      options={[{ _id: "2", name: "" }]}
                      value={data.Communicates["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Communicates", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Communicates"
                      options={[{ _id: "3", name: "" }]}
                      value={data.Communicates["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Communicates", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Communicates"
                      options={[{ _id: "4", name: "" }]}
                      value={data.Communicates["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Communicates", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Communicates"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.Communicates}
                      value={data.Communicates["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Understands verbal communication
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Understands", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Understands"
                      options={[{ _id: "1", name: "" }]}
                      value={data.Understands["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Understands", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Understands"
                      options={[{ _id: "2", name: "" }]}
                      value={data.Understands["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Understands", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Understands"
                      options={[{ _id: "3", name: "" }]}
                      value={data.Understands["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Understands", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Understands"
                      options={[{ _id: "4", name: "" }]}
                      value={data.Understands["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Understands", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Understands"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.Understands}
                      value={data.Understands["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Appropriate volume
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateVolume",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateVolume"
                      options={[{ _id: "1", name: "" }]}
                      value={data.AppropriateVolume["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateVolume",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateVolume"
                      options={[{ _id: "2", name: "" }]}
                      value={data.AppropriateVolume["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateVolume",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateVolume"
                      options={[{ _id: "3", name: "" }]}
                      value={data.AppropriateVolume["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateVolume",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateVolume"
                      options={[{ _id: "4", name: "" }]}
                      value={data.AppropriateVolume["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "AppropriateVolume",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateVolume"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.AppropriateVolume}
                      value={data.AppropriateVolume["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Appropriate speed
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateSpeed", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateSpeed"
                      options={[{ _id: "1", name: "" }]}
                      value={data.AppropriateSpeed["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateSpeed", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateSpeed"
                      options={[{ _id: "2", name: "" }]}
                      value={data.AppropriateSpeed["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateSpeed", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateSpeed"
                      options={[{ _id: "3", name: "" }]}
                      value={data.AppropriateSpeed["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateSpeed", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateSpeed"
                      options={[{ _id: "4", name: "" }]}
                      value={data.AppropriateSpeed["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateSpeed", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateSpeed"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.AppropriateSpeed}
                      value={data.AppropriateSpeed["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Speaks clearly
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SpeaksClearly", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SpeaksClearly"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SpeaksClearly["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SpeaksClearly", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SpeaksClearly"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SpeaksClearly["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SpeaksClearly", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SpeaksClearly"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SpeaksClearly["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SpeaksClearly", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SpeaksClearly"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SpeaksClearly["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SpeaksClearly", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SpeaksClearly"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SpeaksClearly}
                      value={data.SpeaksClearly["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Speaks in whole sentences
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SpeaksSentences", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SpeaksSentences"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SpeaksSentences["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SpeaksSentences", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SpeaksSentences"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SpeaksSentences["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SpeaksSentences", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SpeaksSentences"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SpeaksSentences["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SpeaksSentences", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SpeaksSentences"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SpeaksSentences["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SpeaksSentences", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SpeaksSentences"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SpeaksSentences}
                      value={data.SpeaksSentences["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Interaction Skills</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Gets along with others
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "GetsAlong", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="GetsAlong"
                      options={[{ _id: "1", name: "" }]}
                      value={data.GetsAlong["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "GetsAlong", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="GetsAlong"
                      options={[{ _id: "2", name: "" }]}
                      value={data.GetsAlong["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "GetsAlong", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="GetsAlong"
                      options={[{ _id: "3", name: "" }]}
                      value={data.GetsAlong["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "GetsAlong", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="GetsAlong"
                      options={[{ _id: "4", name: "" }]}
                      value={data.GetsAlong["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "GetsAlong", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="GetsAlong"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.GetsAlong}
                      value={data.GetsAlong["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Participates in social activities
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Participates", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Participates"
                      options={[{ _id: "1", name: "" }]}
                      value={data.Participates["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Participates", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Participates"
                      options={[{ _id: "2", name: "" }]}
                      value={data.Participates["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Participates", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Participates"
                      options={[{ _id: "3", name: "" }]}
                      value={data.Participates["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Participates", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Participates"
                      options={[{ _id: "4", name: "" }]}
                      value={data.Participates["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Participates", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Participates"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.Participates}
                      value={data.Participates["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Initiates conversation
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "InitiatesConvo", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="InitiatesConvo"
                      options={[{ _id: "1", name: "" }]}
                      value={data.InitiatesConvo["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "InitiatesConvo", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="InitiatesConvo"
                      options={[{ _id: "2", name: "" }]}
                      value={data.InitiatesConvo["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "InitiatesConvo", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="InitiatesConvo"
                      options={[{ _id: "3", name: "" }]}
                      value={data.InitiatesConvo["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "InitiatesConvo", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="InitiatesConvo"
                      options={[{ _id: "4", name: "" }]}
                      value={data.InitiatesConvo["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "InitiatesConvo", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="InitiatesConvo"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.InitiatesConvo}
                      value={data.InitiatesConvo["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Relates past events in factual manner
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "RelatesPastEvents",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="RelatesPastEvents"
                      options={[{ _id: "1", name: "" }]}
                      value={data.RelatesPastEvents["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "RelatesPastEvents",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="RelatesPastEvents"
                      options={[{ _id: "2", name: "" }]}
                      value={data.RelatesPastEvents["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "RelatesPastEvents",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="RelatesPastEvents"
                      options={[{ _id: "3", name: "" }]}
                      value={data.RelatesPastEvents["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "RelatesPastEvents",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="RelatesPastEvents"
                      options={[{ _id: "4", name: "" }]}
                      value={data.RelatesPastEvents["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "RelatesPastEvents",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="RelatesPastEvents"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.RelatesPastEvents}
                      value={data.RelatesPastEvents["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Identifies person by names
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesPeople", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesPeople"
                      options={[{ _id: "1", name: "" }]}
                      value={data.IdentifiesPeople["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesPeople", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesPeople"
                      options={[{ _id: "2", name: "" }]}
                      value={data.IdentifiesPeople["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesPeople", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesPeople"
                      options={[{ _id: "3", name: "" }]}
                      value={data.IdentifiesPeople["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesPeople", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesPeople"
                      options={[{ _id: "4", name: "" }]}
                      value={data.IdentifiesPeople["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesPeople", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesPeople"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.IdentifiesPeople}
                      value={data.IdentifiesPeople["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Listens without interrupting
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Listens", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Listens"
                      options={[{ _id: "1", name: "" }]}
                      value={data.Listens["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Listens", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Listens"
                      options={[{ _id: "2", name: "" }]}
                      value={data.Listens["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Listens", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Listens"
                      options={[{ _id: "3", name: "" }]}
                      value={data.Listens["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Listens", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Listens"
                      options={[{ _id: "4", name: "" }]}
                      value={data.Listens["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Listens", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Listens"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.Listens}
                      value={data.Listens["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Maintains eye contact
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "EyeContact", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="EyeContact"
                      options={[{ _id: "1", name: "" }]}
                      value={data.EyeContact["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "EyeContact", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="EyeContact"
                      options={[{ _id: "2", name: "" }]}
                      value={data.EyeContact["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "EyeContact", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="EyeContact"
                      options={[{ _id: "3", name: "" }]}
                      value={data.EyeContact["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "EyeContact", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="EyeContact"
                      options={[{ _id: "4", name: "" }]}
                      value={data.EyeContact["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "EyeContact", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="EyeContact"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.EyeContact}
                      value={data.EyeContact["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Verbalizes name correctly
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizesName", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesName"
                      options={[{ _id: "1", name: "" }]}
                      value={data.VerbalizesName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizesName", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesName"
                      options={[{ _id: "2", name: "" }]}
                      value={data.VerbalizesName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizesName", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesName"
                      options={[{ _id: "3", name: "" }]}
                      value={data.VerbalizesName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizesName", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesName"
                      options={[{ _id: "4", name: "" }]}
                      value={data.VerbalizesName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizesName", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesName"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.VerbalizesName}
                      value={data.VerbalizesName["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Maintains personal space
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MaintainsSpace", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MaintainsSpace"
                      options={[{ _id: "1", name: "" }]}
                      value={data.MaintainsSpace["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MaintainsSpace", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MaintainsSpace"
                      options={[{ _id: "2", name: "" }]}
                      value={data.MaintainsSpace["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MaintainsSpace", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MaintainsSpace"
                      options={[{ _id: "3", name: "" }]}
                      value={data.MaintainsSpace["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MaintainsSpace", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MaintainsSpace"
                      options={[{ _id: "4", name: "" }]}
                      value={data.MaintainsSpace["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MaintainsSpace", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MaintainsSpace"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.MaintainsSpace}
                      value={data.MaintainsSpace["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Appropriate with touch
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateTouch", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateTouch"
                      options={[{ _id: "1", name: "" }]}
                      value={data.AppropriateTouch["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateTouch", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateTouch"
                      options={[{ _id: "2", name: "" }]}
                      value={data.AppropriateTouch["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateTouch", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateTouch"
                      options={[{ _id: "3", name: "" }]}
                      value={data.AppropriateTouch["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateTouch", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateTouch"
                      options={[{ _id: "4", name: "" }]}
                      value={data.AppropriateTouch["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "AppropriateTouch", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="AppropriateTouch"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.AppropriateTouch}
                      value={data.AppropriateTouch["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Controls temper
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ControlsTemper", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ControlsTemper"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ControlsTemper["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ControlsTemper", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ControlsTemper"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ControlsTemper["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ControlsTemper", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ControlsTemper"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ControlsTemper["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ControlsTemper", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ControlsTemper"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ControlsTemper["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ControlsTemper", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ControlsTemper"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ControlsTemper}
                      value={data.ControlsTemper["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Accepts constructive criticism
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "ConstructiveCriticism",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="ConstructiveCriticism"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ConstructiveCriticism["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "ConstructiveCriticism",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="ConstructiveCriticism"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ConstructiveCriticism["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "ConstructiveCriticism",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="ConstructiveCriticism"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ConstructiveCriticism["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "ConstructiveCriticism",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="ConstructiveCriticism"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ConstructiveCriticism["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "ConstructiveCriticism",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="ConstructiveCriticism"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ConstructiveCriticism}
                      value={data.ConstructiveCriticism["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Follows directions
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "FollowsDirections",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowsDirections"
                      options={[{ _id: "1", name: "" }]}
                      value={data.FollowsDirections["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "FollowsDirections",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowsDirections"
                      options={[{ _id: "2", name: "" }]}
                      value={data.FollowsDirections["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "FollowsDirections",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowsDirections"
                      options={[{ _id: "3", name: "" }]}
                      value={data.FollowsDirections["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "FollowsDirections",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowsDirections"
                      options={[{ _id: "4", name: "" }]}
                      value={data.FollowsDirections["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "FollowsDirections",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="FollowsDirections"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.FollowsDirections}
                      value={data.FollowsDirections["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Sound decision making
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DecisionMaking", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DecisionMaking"
                      options={[{ _id: "1", name: "" }]}
                      value={data.DecisionMaking["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DecisionMaking", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DecisionMaking"
                      options={[{ _id: "2", name: "" }]}
                      value={data.DecisionMaking["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DecisionMaking", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DecisionMaking"
                      options={[{ _id: "3", name: "" }]}
                      value={data.DecisionMaking["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DecisionMaking", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DecisionMaking"
                      options={[{ _id: "4", name: "" }]}
                      value={data.DecisionMaking["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "DecisionMaking", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="DecisionMaking"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.DecisionMaking}
                      value={data.DecisionMaking["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Stress management
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StressManagement", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StressManagement"
                      options={[{ _id: "1", name: "" }]}
                      value={data.StressManagement["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StressManagement", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StressManagement"
                      options={[{ _id: "2", name: "" }]}
                      value={data.StressManagement["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StressManagement", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StressManagement"
                      options={[{ _id: "3", name: "" }]}
                      value={data.StressManagement["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StressManagement", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StressManagement"
                      options={[{ _id: "4", name: "" }]}
                      value={data.StressManagement["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "StressManagement", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="StressManagement"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.StressManagement}
                      value={data.StressManagement["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Money</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Identifies coins
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesCoins", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesCoins"
                      options={[{ _id: "1", name: "" }]}
                      value={data.IdentifiesCoins["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesCoins", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesCoins"
                      options={[{ _id: "2", name: "" }]}
                      value={data.IdentifiesCoins["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesCoins", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesCoins"
                      options={[{ _id: "3", name: "" }]}
                      value={data.IdentifiesCoins["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesCoins", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesCoins"
                      options={[{ _id: "4", name: "" }]}
                      value={data.IdentifiesCoins["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "IdentifiesCoins", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="IdentifiesCoins"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.IdentifiesCoins}
                      value={data.IdentifiesCoins["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Verbalizes value of coins/bills
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizesValue", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesValue"
                      options={[{ _id: "1", name: "" }]}
                      value={data.VerbalizesValue["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizesValue", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesValue"
                      options={[{ _id: "2", name: "" }]}
                      value={data.VerbalizesValue["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizesValue", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesValue"
                      options={[{ _id: "3", name: "" }]}
                      value={data.VerbalizesValue["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizesValue", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesValue"
                      options={[{ _id: "4", name: "" }]}
                      value={data.VerbalizesValue["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "VerbalizesValue", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="VerbalizesValue"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.VerbalizesValue}
                      value={data.VerbalizesValue["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Counts money
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CountsMoney", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CountsMoney"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CountsMoney["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CountsMoney", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CountsMoney"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CountsMoney["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CountsMoney", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CountsMoney"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CountsMoney["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CountsMoney", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CountsMoney"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CountsMoney["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CountsMoney", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CountsMoney"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CountsMoney}
                      value={data.CountsMoney["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Makes change
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesChange", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesChange"
                      options={[{ _id: "1", name: "" }]}
                      value={data.MakesChange["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesChange", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesChange"
                      options={[{ _id: "2", name: "" }]}
                      value={data.MakesChange["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesChange", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesChange"
                      options={[{ _id: "3", name: "" }]}
                      value={data.MakesChange["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesChange", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesChange"
                      options={[{ _id: "4", name: "" }]}
                      value={data.MakesChange["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MakesChange", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MakesChange"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.MakesChange}
                      value={data.MakesChange["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Reads price tags
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsPrice", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsPrice"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ReadsPrice["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsPrice", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsPrice"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ReadsPrice["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsPrice", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsPrice"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ReadsPrice["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsPrice", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsPrice"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ReadsPrice["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ReadsPrice", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ReadsPrice"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ReadsPrice}
                      value={data.ReadsPrice["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Completes budget forms
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CompletesBudget", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CompletesBudget"
                      options={[{ _id: "1", name: "" }]}
                      value={data.CompletesBudget["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CompletesBudget", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CompletesBudget"
                      options={[{ _id: "2", name: "" }]}
                      value={data.CompletesBudget["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CompletesBudget", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CompletesBudget"
                      options={[{ _id: "3", name: "" }]}
                      value={data.CompletesBudget["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CompletesBudget", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CompletesBudget"
                      options={[{ _id: "4", name: "" }]}
                      value={data.CompletesBudget["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "CompletesBudget", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="CompletesBudget"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.CompletesBudget}
                      value={data.CompletesBudget["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Fills out banking slips
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BankingSlips", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BankingSlips"
                      options={[{ _id: "1", name: "" }]}
                      value={data.BankingSlips["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BankingSlips", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BankingSlips"
                      options={[{ _id: "2", name: "" }]}
                      value={data.BankingSlips["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BankingSlips", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BankingSlips"
                      options={[{ _id: "3", name: "" }]}
                      value={data.BankingSlips["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BankingSlips", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BankingSlips"
                      options={[{ _id: "4", name: "" }]}
                      value={data.BankingSlips["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BankingSlips", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BankingSlips"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.BankingSlips}
                      value={data.BankingSlips["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Opens saving/checking account
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OpenAccount", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OpenAccount"
                      options={[{ _id: "1", name: "" }]}
                      value={data.OpenAccount["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OpenAccount", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OpenAccount"
                      options={[{ _id: "2", name: "" }]}
                      value={data.OpenAccount["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OpenAccount", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OpenAccount"
                      options={[{ _id: "3", name: "" }]}
                      value={data.OpenAccount["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OpenAccount", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OpenAccount"
                      options={[{ _id: "4", name: "" }]}
                      value={data.OpenAccount["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "OpenAccount", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="OpenAccount"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.OpenAccount}
                      value={data.OpenAccount["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Purchases money orders
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MoneyOrders", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MoneyOrders"
                      options={[{ _id: "1", name: "" }]}
                      value={data.MoneyOrders["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MoneyOrders", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MoneyOrders"
                      options={[{ _id: "2", name: "" }]}
                      value={data.MoneyOrders["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MoneyOrders", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MoneyOrders"
                      options={[{ _id: "3", name: "" }]}
                      value={data.MoneyOrders["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MoneyOrders", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MoneyOrders"
                      options={[{ _id: "4", name: "" }]}
                      value={data.MoneyOrders["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "MoneyOrders", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="MoneyOrders"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.MoneyOrders}
                      value={data.MoneyOrders["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Writes checks
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WritesChecks", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WritesChecks"
                      options={[{ _id: "1", name: "" }]}
                      value={data.WritesChecks["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WritesChecks", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WritesChecks"
                      options={[{ _id: "2", name: "" }]}
                      value={data.WritesChecks["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WritesChecks", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WritesChecks"
                      options={[{ _id: "3", name: "" }]}
                      value={data.WritesChecks["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WritesChecks", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WritesChecks"
                      options={[{ _id: "4", name: "" }]}
                      value={data.WritesChecks["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "WritesChecks", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="WritesChecks"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.WritesChecks}
                      value={data.WritesChecks["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Balances checkbook
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BalanceCheckbook", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BalanceCheckbook"
                      options={[{ _id: "1", name: "" }]}
                      value={data.BalanceCheckbook["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BalanceCheckbook", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BalanceCheckbook"
                      options={[{ _id: "2", name: "" }]}
                      value={data.BalanceCheckbook["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BalanceCheckbook", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BalanceCheckbook"
                      options={[{ _id: "3", name: "" }]}
                      value={data.BalanceCheckbook["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BalanceCheckbook", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BalanceCheckbook"
                      options={[{ _id: "4", name: "" }]}
                      value={data.BalanceCheckbook["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "BalanceCheckbook", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="BalanceCheckbook"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.BalanceCheckbook}
                      value={data.BalanceCheckbook["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Job Seeking Skills</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Utilizes help of Job Coach or BVR
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobCoach", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobCoach"
                      options={[{ _id: "1", name: "" }]}
                      value={data.JobCoach["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobCoach", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobCoach"
                      options={[{ _id: "2", name: "" }]}
                      value={data.JobCoach["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobCoach", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobCoach"
                      options={[{ _id: "3", name: "" }]}
                      value={data.JobCoach["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobCoach", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobCoach"
                      options={[{ _id: "4", name: "" }]}
                      value={data.JobCoach["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobCoach", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobCoach"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.JobCoach}
                      value={data.JobCoach["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Can fill out job resume
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobResume", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobResume"
                      options={[{ _id: "1", name: "" }]}
                      value={data.JobResume["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobResume", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobResume"
                      options={[{ _id: "2", name: "" }]}
                      value={data.JobResume["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobResume", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobResume"
                      options={[{ _id: "3", name: "" }]}
                      value={data.JobResume["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobResume", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobResume"
                      options={[{ _id: "4", name: "" }]}
                      value={data.JobResume["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobResume", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobResume"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.JobResume}
                      value={data.JobResume["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Can interview for job
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobInterview", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobInterview"
                      options={[{ _id: "1", name: "" }]}
                      value={data.JobInterview["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobInterview", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobInterview"
                      options={[{ _id: "2", name: "" }]}
                      value={data.JobInterview["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobInterview", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobInterview"
                      options={[{ _id: "3", name: "" }]}
                      value={data.JobInterview["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobInterview", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobInterview"
                      options={[{ _id: "4", name: "" }]}
                      value={data.JobInterview["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobInterview", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobInterview"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.JobInterview}
                      value={data.JobInterview["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Knows how to read want ads for job
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobAds", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobAds"
                      options={[{ _id: "1", name: "" }]}
                      value={data.JobAds["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobAds", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobAds"
                      options={[{ _id: "2", name: "" }]}
                      value={data.JobAds["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobAds", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobAds"
                      options={[{ _id: "3", name: "" }]}
                      value={data.JobAds["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobAds", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobAds"
                      options={[{ _id: "4", name: "" }]}
                      value={data.JobAds["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "JobAds", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="JobAds"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.JobAds}
                      value={data.JobAds["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Job Maintenance Skills</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Sets alarm on clock and rises
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsAlarm", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsAlarm"
                      options={[{ _id: "1", name: "" }]}
                      value={data.SetsAlarm["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsAlarm", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsAlarm"
                      options={[{ _id: "2", name: "" }]}
                      value={data.SetsAlarm["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsAlarm", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsAlarm"
                      options={[{ _id: "3", name: "" }]}
                      value={data.SetsAlarm["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsAlarm", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsAlarm"
                      options={[{ _id: "4", name: "" }]}
                      value={data.SetsAlarm["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "SetsAlarm", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="SetsAlarm"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.SetsAlarm}
                      value={data.SetsAlarm["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Is punctual for appointment
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Punctual", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Punctual"
                      options={[{ _id: "1", name: "" }]}
                      value={data.Punctual["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Punctual", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Punctual"
                      options={[{ _id: "2", name: "" }]}
                      value={data.Punctual["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Punctual", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Punctual"
                      options={[{ _id: "3", name: "" }]}
                      value={data.Punctual["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Punctual", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Punctual"
                      options={[{ _id: "4", name: "" }]}
                      value={data.Punctual["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "Punctual", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="Punctual"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.Punctual}
                      value={data.Punctual["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Follows directions of authority figures
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ObeysAuthority", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ObeysAuthority"
                      options={[{ _id: "1", name: "" }]}
                      value={data.ObeysAuthority["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ObeysAuthority", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ObeysAuthority"
                      options={[{ _id: "2", name: "" }]}
                      value={data.ObeysAuthority["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ObeysAuthority", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ObeysAuthority"
                      options={[{ _id: "3", name: "" }]}
                      value={data.ObeysAuthority["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ObeysAuthority", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ObeysAuthority"
                      options={[{ _id: "4", name: "" }]}
                      value={data.ObeysAuthority["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "ObeysAuthority", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="ObeysAuthority"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.ObeysAuthority}
                      value={data.ObeysAuthority["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Understands worker/supervisor relationship
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UnderstandsWorkRelationships",
                          value: "1",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UnderstandsWorkRelationships"
                      options={[{ _id: "1", name: "" }]}
                      value={data.UnderstandsWorkRelationships["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UnderstandsWorkRelationships",
                          value: "2",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UnderstandsWorkRelationships"
                      options={[{ _id: "2", name: "" }]}
                      value={data.UnderstandsWorkRelationships["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UnderstandsWorkRelationships",
                          value: "3",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UnderstandsWorkRelationships"
                      options={[{ _id: "3", name: "" }]}
                      value={data.UnderstandsWorkRelationships["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UnderstandsWorkRelationships",
                          value: "4",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UnderstandsWorkRelationships"
                      options={[{ _id: "4", name: "" }]}
                      value={data.UnderstandsWorkRelationships["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: {
                          name: "UnderstandsWorkRelationships",
                          value: "0",
                        },
                      });
                    }}
                  >
                    <RadioButton
                      name="UnderstandsWorkRelationships"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.UnderstandsWorkRelationships}
                      value={data.UnderstandsWorkRelationships["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-start">
                  <div className="col border-end border-bottom bg-light py-2">
                    <b>Legal Skills</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Knows name of Guardian ad Litem
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsGALName", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsGALName"
                      options={[{ _id: "1", name: "" }]}
                      value={data.KnowsGALName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsGALName", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsGALName"
                      options={[{ _id: "2", name: "" }]}
                      value={data.KnowsGALName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsGALName", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsGALName"
                      options={[{ _id: "3", name: "" }]}
                      value={data.KnowsGALName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsGALName", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsGALName"
                      options={[{ _id: "4", name: "" }]}
                      value={data.KnowsGALName["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsGALName", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsGALName"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.KnowsGALName}
                      value={data.KnowsGALName["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-3 border-end border-bottom border-start">
                    Knows basic legal rights
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsBasicRights", value: "1" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsBasicRights"
                      options={[{ _id: "1", name: "" }]}
                      value={data.KnowsBasicRights["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsBasicRights", value: "2" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsBasicRights"
                      options={[{ _id: "2", name: "" }]}
                      value={data.KnowsBasicRights["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsBasicRights", value: "3" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsBasicRights"
                      options={[{ _id: "3", name: "" }]}
                      value={data.KnowsBasicRights["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsBasicRights", value: "4" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsBasicRights"
                      options={[{ _id: "4", name: "" }]}
                      value={data.KnowsBasicRights["@value"]}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div
                    className="col border-end border-bottom pt-3 text-center"
                    onClick={() => {
                      handleChange({
                        currentTarget: { name: "KnowsBasicRights", value: "0" },
                      });
                    }}
                  >
                    <RadioButton
                      name="KnowsBasicRights"
                      options={[{ _id: "0", name: "" }]}
                      error={errors.KnowsBasicRights}
                      value={data.KnowsBasicRights["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <br />
              <TextArea
                name="OtherDetails"
                label="Other (Assessments, Pertinent Information, etc.)"
                error={errors.OtherDetails}
                value={data.OtherDetails["@value"]}
                onChange={handleChange}
                identical={compare("OtherDetails")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="row">
                <div className="col">
                  <Input
                    name="PersonCompletingForm"
                    label="Person Completing Form"
                    error={errors.PersonCompletingForm}
                    value={data.PersonCompletingForm["@value"]}
                    onChange={handleChange}
                    identical={compare("PersonCompletingForm")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="Date"
                    label="Date"
                    type="date"
                    error={errors.Date}
                    value={data.Date["@value"]}
                    onChange={handleChange}
                    identical={compare("Date")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default KYF176;
